/**
 * Front-end view types for the staff/admin/tracking surfaces. These mirror the intended API
 * response shapes (aligned with spec §5 / ADR-0002 + the P3 "newer decisions": Attachment
 * sub-entity, Property, typed+visibility attachments). The Go backend (inky/go-backend) will
 * return these; until then mockDb.ts fabricates them. Reporter PII lives ONLY on the staff-side
 * `StaffRequestDetail` — the public `PublicRequestView` (from @td/shared) structurally omits it.
 */
import type { RequestStatus } from "@td/shared";
import type { SLAStage } from "../lib/sla.js";

export type Visibility = "public" | "internal";

export type AttachmentKind =
  "resident-photo" | "site-photo" | "letter-sent" | "certified-mail" | "document" | "other";

export interface AttachmentView {
  id: string;
  /** Signed URL for thumbnail/download (NFR-10). Mocked to a data/placeholder URL here. */
  url: string;
  filename: string;
  contentType: string;
  kind: AttachmentKind;
  visibility: Visibility;
  /** Staff display name, or null when resident-uploaded. */
  uploaderName?: string | null;
  note?: string;
  /** Certified-mail tracking number, when kind = certified-mail. */
  trackingNumber?: string;
  createdAt: string;
  /** True for images (render a thumbnail); false → file glyph. */
  isImage: boolean;
}

export type AuditType =
  | "status_change"
  | "note"
  | "assignment"
  | "notification_sent"
  | "attachment"
  | "created"
  | "resident_comment"
  /** Permits & Licensing (roadmap module 6) — a staff-recorded pass/fail inspection outcome
   *  (shared.EventInspection mirror); does not itself change the permit's status. */
  | "inspection";

export interface AuditEventView {
  id: string;
  type: AuditType;
  visibility: Visibility;
  body: string;
  /** Staff display name / "System" / "Resident" (resident-authored, Source="resident") / null
   *  (unattributed). Resident-sourced events are always mapped to the literal "Resident" — never
   *  left blank — so the staff timeline can't render an empty actor line. */
  actorName?: string | null;
  /** For status_change events, the resulting status — RequestStatus for concern/question rows;
   *  KORAStatus/CaseStatus/PermitStatus (their own disjoint value-spaces) for KORA/case/permit rows
   *  (records-request.md / Code Enforcement / Permits & Licensing). */
  toStatus?: RequestStatus | KORAStatus | CaseStatus | PermitStatus;
  createdAt: string;
}

export interface ReporterContactView {
  name?: string;
  email?: string;
  phone?: string;
}

export interface PropertyView {
  id: string;
  canonicalAddress: string;
  priorConcerns: Array<{
    id: string;
    trackingCode: string;
    category: string;
    status: RequestStatus;
    createdAt: string;
  }>;
}

/** Staff-side full detail — includes PII + internal data (authenticated, tenant-scoped). */
export interface StaffRequestDetail {
  id: string;
  trackingCode: string;
  category: string;
  /** Friendly category label (shared.ResolveCategoryLabel), e.g. "Junk / abandoned vehicle" for the
   *  raw category id "junk-vehicle". Falls back to `category` in the UI when absent/unresolved. */
  categoryLabel?: string;
  /** Public-safe controlled-vocabulary concern tag (e.g. "Meter location"); omitted when untagged. */
  issueType?: string;
  description: string;
  status: RequestStatus;
  location: { address: string; lat?: number; lng?: number };
  reporterContact: ReporterContactView | null;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: AuditEventView[];
  property: PropertyView | null;
  createdAt: string;
  updatedAt: string;
  /** Work-item due date (work-item-due-dates.md) — StaffOnly `Kind=concern` categories only
   *  (e.g. "Flag Program", "Work Orders / Maintenance"). "YYYY-MM-DD", or "" / undefined for "no
   *  due date". Additive on every concern, but only ever shown/edited for a StaffOnly category —
   *  non-StaffOnly (citizen) concerns keep the ack/resolve SLA and never surface this field. */
  dueDate?: string;
}

/** Compact row for the queue table. */
export interface QueueRow {
  id: string;
  trackingCode: string;
  category: string;
  /** Friendly category label (backend shared.ResolveCategoryLabel); falls back to `category` if absent. */
  categoryLabel?: string;
  /** Public-safe controlled-vocabulary concern tag (e.g. "Meter location"); omitted when untagged. */
  issueType?: string;
  description: string;
  status: RequestStatus;
  address: string;
  attachmentCount: number;
  /** Reporter name (or email, if name-less) — decrypted on the authorized staff-console queue path so
   *  staff can see + search by reporter. `null` when the reporter was anonymous. Never in the export. */
  reporterName: string | null;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
  /** SLA aging (report-a-concern.md "SLA / aging indicators + reminders") — computed read-time,
   *  business days since CreatedAt. Never overdue for a terminal status (shared.IsTerminal). */
  ageBusinessDays: number;
  overdue: boolean;
  /** Which target was breached ("acknowledge" | "resolve"), or "" when not overdue. */
  slaStage: SLAStage;
  /** The business-day target that applies (0 when slaStage is ""). */
  targetDays: number;
  /** Work-item due date (work-item-due-dates.md) — StaffOnly categories only; see
   *  StaffRequestDetail.dueDate. When set, `overdue`/`slaStage` above are driven by THIS field
   *  instead of the business-day SLA (server-computed; see lib/sla.ts evaluateWorkItemDue for the
   *  client-side mirror used by the mock store). */
  dueDate?: string;
}

/** The filter-set a saved view captures — mirrors the queue's own URL params (status/assigneeId/q)
 *  plus the Overdue quick-filter toggle. All optional; a saved view may capture any subset. */
export interface SavedViewFilters {
  status?: string;
  assigneeId?: string;
  q?: string;
  overdue?: boolean;
}

/** A staff member's named, saved queue filter-set (queue power-tools). PRIVATE to the owning staff
 *  user — the server scopes reads/writes to the caller's own views (never another staffer's). */
export interface SavedView {
  id: string;
  name: string;
  filters: SavedViewFilters;
  createdAt: string;
}

/** Per-id outcome of a bulk action (queue power-tools "bulk assign / bulk status") — partial
 *  failures are always visible, never silently dropped. */
export interface BulkItemResult {
  requestId: string;
  ok: boolean;
  reason?: string;
}

export interface StaffUserView {
  id: string;
  name: string | null;
  /** Omitted when the caller is a non-admin STAFF user (GET /api/admin/staff redacts email for
   *  staff callers, admin-only PII) — always present for admin callers. */
  email?: string;
  /** "contributor" (agenda-packet.md §3.2) is the scoped Department Head / Contributor role — a
   *  staff-role Cognito user admitted ONLY to the /api/contrib/* surface (contributorAuth), never
   *  the concern/records/permit/case queues or the admin console's other pages. */
  role: "admin" | "staff" | "contributor";
  status: "active" | "invited" | "deactivated";
  mfaEnrolled: boolean;
  lastActiveAt?: string;
  /** Contributor-only (agenda-packet.md §3.2): the configurable department/role key a Contribution
   *  slot's Department is matched against when AssigneeID is unset. Undefined for admin/staff. */
  department?: string;
  /** RBAC (ADR-0016 §5) group membership — the ids of every Group this user belongs to. Present
   *  only for an admin caller of GET /api/admin/staff (users:read-gated enrichment); undefined for
   *  a non-admin STAFF caller's minimal projection. */
  groupIds?: string[];
  /** The UNION of every group's Permissions this user belongs to — "module:action" strings, e.g.
   *  "meetings:write". Same admin-only enrichment as groupIds. */
  effectivePermissions?: string[];
}

/** RBAC (ADR-0016 §2/§5) tenant-defined permission bundle. A user's effective permissions are the
 *  union of every group in their StaffUserView.groupIds. */
export interface GroupView {
  id: string;
  name: string;
  description: string;
  /** "module:action" strings — always validated against the live PermissionsRegistry on write. */
  permissions: string[];
  /** True for the protected Admin template/migration group — cannot be deleted, and its
   *  permissions can't be narrowed below "every permission" (ADR-0016 §6). */
  system: boolean;
}

/** One module's row in the permission matrix — GET /api/admin/permissions/registry (ADR-0016 §1),
 *  the SINGLE source of truth for which actions are valid for which module (never hand-duplicated
 *  in the frontend). */
export interface PermissionsRegistryModule {
  module: string;
  actions: string[];
}

/** "concern" (default/omitted) | "question" | "channel" | "kora" — Category is reused as the topic
 *  taxonomy for intake/subscription types (ask-a-question-faq.md §2.1, stay-informed-channels.md
 *  §2.1, records-request.md §KORA record types); concern categories, question topics, subscription
 *  channels, AND KORA record-type taxonomy are the SAME entity, filtered by Kind. Mirrors
 *  shared.Category.Kind / shared.NormalizeKind's zero-value rule: an absent kind reads as "concern"
 *  so every pre-Ask+FAQ/pre-Channels/pre-KORA category is unambiguously unaffected. */
export type CategoryKind = "concern" | "question" | "channel" | "kora" | "case" | "permit" | "trade";

export interface CategoryView {
  id: string;
  label: string;
  department: string;
  order: number;
  active: boolean;
  /** Optional per-category SLA overrides (business days); undefined/null = inherit the tenant's
   *  base target (report-a-concern.md "SLA / aging indicators + reminders"). Blank in the admin UI
   *  means inherit. */
  slaAckDays?: number | null;
  slaResolveDays?: number | null;
  /** Per-category auto-routing target (config.md "Per-category routing rules / auto-assignment") —
   *  the id of an active StaffUser in this tenant to auto-assign new requests in this category to.
   *  null/undefined = no per-category routing (falls through to the tenant default assignee). */
  defaultAssigneeId?: string | null;
  /** Concern category vs. question topic vs. channel (see CategoryKind doc). Undefined reads as
   *  "concern" — callers that need to branch on it should treat a missing value as "concern", never
   *  "unknown". */
  kind?: CategoryKind;
  /** Channel-only (stay-informed-channels.md §2.1): pre-checked on the subscribe form. Undefined/
   *  false for concern/question rows — never meaningful outside Kind="channel". */
  defaultOn?: boolean;
  /** PERMIT-TYPE-ONLY (Kind="permit" — permits-fidelity.md §A.1): the versioned per-type guided
   *  application form. Undefined for every other Kind, and for a legacy permit type seeded before
   *  Phase A (the apply page falls back to the flexible free-text form for those). */
  formSchema?: FormSchema;
  /** PERMIT-TYPE-ONLY: the required-attachment/affidavit/prereq checklist for this type. Undefined/
   *  empty for every other Kind or a legacy permit type. */
  requiredDocs?: RequiredDoc[];
  /** PERMIT-TYPE-ONLY: per-permit-type application fee override (dollars). undefined/null = inherit
   *  the tenant-wide default application fee; `0` = free; any other number = that custom amount.
   *  Blank in the admin UI means inherit — mirrors slaAckDays/slaResolveDays's blank=inherit UX.
   *  Undefined for every other Kind. */
  permitAppFeeOverride?: number | null;
  /** CONCERN-ONLY (staff-only-concern-categories): an internal work-item category (e.g. "Flag
   *  Program", "Work Orders / Maintenance") — hidden from the public embed-config/report form and
   *  rejected by the public submit path server-side, but still selectable from the staff "Log a
   *  Concern" form. Undefined/false for every other Kind, and for a normal public concern category. */
  staffOnly?: boolean;
  /** CONCERN-ONLY (public-concerns-map.md / ADR-0023): per-category opt-in to the public "Reported
   *  Concerns" map/feed. Default false/undefined — a concern category is INVISIBLE to
   *  GET /api/public/concerns until a staff admin explicitly turns this on (opposite default
   *  direction from staffOnly: staffOnly hides-from-public by default-false-means-VISIBLE,
   *  showOnMap hides-from-the-map by default-false-means-HIDDEN). Meaningless for any other Kind. */
  showOnMap?: boolean;
  /** PERMIT-TYPE-ONLY (permit-inspections.md Phase 1): the ordered list of expected inspection
   *  slots this type's checklist renders (staff detail + resident tracking). Undefined/empty means
   *  "no checklist" (e.g. Water) — a fully valid state, same "fields that don't apply to this Kind
   *  stay nil" discipline as formSchema/requiredDocs. Undefined for every other Kind. */
  inspectionTypes?: InspectionTypeDef[];
}

export type NotificationTrigger = "confirmation" | "status-update" | "resolution";

export interface NotificationTemplateView {
  trigger: NotificationTrigger;
  emailSubject: string;
  emailBody: string;
  smsBody: string;
  smsEnabled: boolean;
}

// ---- Notify (ADR-0012 / docs/design/notify/spec.md) ------------------------

/** Subscription lifecycle (shared.SubscriptionStatus mirror). */
export type SubscriptionStatus =
  "pending" | "confirmed" | "unsubscribed" | "bounced" | "complained";

/** Broadcast lifecycle (shared.BroadcastStatus mirror). */
export type BroadcastStatus = "draft" | "sending" | "sent";

/** Result of POST /api/public/notify/subscribe — always success-shaped (anti-enumeration,
 *  spec §1.6): the UI never learns whether the email was already subscribed. */
export interface SubscribeResult {
  status: "pending" | "confirmed";
}

// ---- Postal (snail-mail) updates (docs/features/postal-updates.md) ---------------------------
//
// A front-end + CRM-enrichment extension of Subscribe (ADR-0011 "modules sell separately but never
// store data separately") — NOT a new module. Adds a delivery-method choice (email/mail/both) and a
// structured mailing address to the existing Subscribe/Preferences flows.

/** "email" | "mail" | "both" — how a subscriber wants to hear from the city (postal-updates.md).
 *  Empty/omitted on the wire normalizes to "email" (backward-compatible with pre-postal subs). */
export type DeliveryMethod = "email" | "mail" | "both";

/** Structured postal address (shared.PostalAddress mirror) — line1/city/state/zip required when
 *  captured; line2 optional. State is 2-letter USPS, zip is 5 or 9-digit (zip+4). */
export interface MailingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
}

/** The resolved state of a confirm/unsubscribe capability-URL landing page (spec §2.2/§3.2). */
export type NotifyLandingState = "success" | "already" | "expired" | "unsubscribed-since";

export interface NotifyLandingResult {
  state: NotifyLandingState;
}

// ---- Stay Informed — topic channels (docs/architecture/stay-informed-channels.md) -------------
//
// Channel = a Category row with Kind="channel" (§2.1) — SAME entity as concern categories/question
// topics, reused. The public embed-config response carries `channels` as active Kind=channel rows
// only (server-split, handler/public.go EmbedConfig), so this list can never mix concern/question
// categories.

/** An active "what updates do you want" channel (subscribe checkbox / preferences page). */
export interface ChannelView {
  id: string;
  label: string;
  order: number;
  /** Pre-checked on the subscribe form (stay-informed-channels.md §2.1) — channel-only field. */
  defaultOn: boolean;
}

/** The resolved state of the tokenised preferences page (stay-informed-channels.md §3.2) — mirrors
 *  NotifyLandingState's anti-enumeration vocabulary plus the post-save "unsubscribed" outcome for
 *  the uncheck-all → real-unsubscribe path. */
export type PreferencesState = "success" | "expired" | "unsubscribed-since" | "unsubscribed";

/** Result of GET /api/public/preferences?token=. `allChannels` mirrors the backend's "empty
 *  Channels = subscribed to everything" rule (§2.2) — true when `selected` is empty/nil, so the UI
 *  can render "you're getting everything" rather than an all-unchecked list. `deliveryMethod`/
 *  `mailingAddress` are the postal-updates.md extension — `deliveryMethod` is NEVER "" here (the
 *  server normalizes empty to "email"); `mailingAddress` is null when none is on file. */
export interface PreferencesResult {
  state: PreferencesState;
  channels?: ChannelView[];
  selected?: string[];
  allChannels?: boolean;
  deliveryMethod?: DeliveryMethod;
  mailingAddress?: MailingAddress | null;
}

/** Result of PUT /api/public/preferences?token=. */
export interface PreferencesUpdateResult {
  state: PreferencesState;
  selected?: string[];
  deliveryMethod?: DeliveryMethod;
  mailingAddress?: MailingAddress | null;
}

/** Staff subscriber console row (subscriber-self-service.md Part B — GET /api/admin/subscribers).
 *  PII (name/email/hasMailingAddress) is admin-gated server-side, never exposed publicly.
 *  `deliveryMethod` is never "" on this wire shape — the server normalizes empty to "email". */
export interface SubscriberView {
  id: string;
  name?: string;
  email?: string;
  status: SubscriptionStatus;
  deliveryMethod: DeliveryMethod;
  channels: string[];
  hasMailingAddress: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Announcements channel flags (turnkey-cms-ui.md §5.1) — added to the existing Notify Broadcast
 *  compose console, NOT a new editor. Independent toggles (a clerk may pick any combination,
 *  including all three or just one); `channelEmail` defaults true (the unchanged v1 behavior a town
 *  that never touches the new toggles keeps getting). Contract per the PA task brief: Inky lands
 *  matching `channelEmail`/`channelWebsite`/`channelAlert` fields on `CreateBroadcastInput`/
 *  `Broadcast` in `services/api`/`@td/shared` in parallel — AS OF THIS BUILD that landing has not
 *  reached this worktree/branch yet (no `channelEmail` et al. anywhere in services/api or
 *  packages/shared here), so `saveBroadcastDraftLive`/`sendBroadcastLive` (staffApi.ts) currently
 *  send/read these fields optimistically; the live API will ignore unknown JSON fields on write and
 *  the read-mapper defaults them when absent (see mapBroadcastDetail) — verify this contract lands
 *  with matching field names before this ships past mock mode. */
export interface BroadcastChannelFlags {
  channelEmail: boolean;
  channelWebsite: boolean;
  channelAlert: boolean;
  /** ISO date — only meaningful when channelAlert is true; defaults to 7 days from compose time
   *  (turnkey-cms-ui.md §5.1). */
  alertShowUntil?: string | null;
}

/** Compact row for the staff Broadcast list (spec §4.1). */
export interface BroadcastRow extends BroadcastChannelFlags {
  id: string;
  subject: string;
  status: BroadcastStatus;
  audience: string;
  sentCount: number;
  bouncedCount: number;
  complainedCount: number;
  /** Unsubscribed-as-a-result-of-this-send count; not tracked separately server-side in v1, so
   *  the UI computes/omits it — kept optional so a future field slots in without a shape change. */
  unsubscribedCount?: number;
  /** Website-post publish outcome for this send (turnkey-cms-ui.md §5.4) — a COMPOUND state,
   *  reported separately from email delivery since one channel can succeed while the other fails.
   *  Absent/undefined when channelWebsite was never on for this broadcast. */
  websitePostStatus?: "posted" | "failed" | "removed-early";
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
}

/** Full broadcast detail — draft (editable) or sent (read-only), spec §4.2/§4.5. */
export interface BroadcastDetail extends BroadcastRow {
  body: string;
  createdBy: string;
}

export interface CreateBroadcastInput extends Partial<BroadcastChannelFlags> {
  subject: string;
  body: string;
  /** "all" (default/omitted) | "channel:<id>" (stay-informed-channels.md §2.3/§3.3) — the target
   *  audience for this draft. Omitted means "all confirmed subscribers", unchanged v1 default. */
  audience?: string;
}

export interface SendBroadcastResult {
  status: BroadcastStatus;
  recipientCount: number;
}

// ---- Analytics (GET /api/analytics, staff-auth, tenant-scoped from JWT) ---------------------
//
// AGGREGATE-ONLY — never carries reporter PII, contact info, or individual descriptions. Every
// field here is a count, an average, or a labeled bucket.

export interface AnalyticsRange {
  start: string;
  end: string;
}

export interface AnalyticsTotals {
  allTime: number;
  inRange: number;
  open: number;
  overdueCount: number;
}

export interface AnalyticsAverages {
  avgBusinessDaysToAcknowledge: number | null;
  avgBusinessDaysToResolve: number | null;
  acknowledgedSampleSize: number;
  resolvedSampleSize: number;
}

export interface CategoryCount {
  categoryLabel: string;
  count: number;
}

export interface VolumeTrendPoint {
  weekStart: string;
  count: number;
}

export interface AnalyticsView {
  range: AnalyticsRange;
  totals: AnalyticsTotals;
  averages: AnalyticsAverages;
  /** -1 sentinel = no resolved-in-range data to compute a percentage from. */
  onTimeResolvedPct: number;
  onTimeResolvedSampleSize: number;
  byStatus: Partial<Record<RequestStatus, number>>;
  /** Top 8 categories by count + a trailing "Other" bucket. */
  byCategory: CategoryCount[];
  /** Ascending weekly buckets. */
  volumeTrend: VolumeTrendPoint[];
  /** FAQ deflection-loop block (ask-a-question-faq.md §5.5) — optional so the mayor dashboard
   *  doesn't need a shape change when the backend hasn't wired it in yet. */
  faq?: FAQAnalytics;
}

// ---- Ask a Question + FAQ (ask-a-question-faq.md) --------------------------

/** Question lifecycle (shared.QuestionStatuses mirror) — distinct from RequestStatus. */
export type QuestionStatus = "QNew" | "QAnswered" | "QPublished" | "QClosed";

export const QUESTION_STATUS_LABELS: Record<QuestionStatus, string> = {
  QNew: "New",
  QAnswered: "Answered",
  QPublished: "Published",
  QClosed: "Closed",
};

/** Compact row for the staff question console queue (♻️ QueueRow shape, topicLabel instead of
 *  categoryLabel/SLA — questions have no SLA aging in v1). */
export interface QuestionQueueRow {
  id: string;
  trackingCode: string;
  topic: string;
  topicLabel?: string;
  question: string;
  status: QuestionStatus;
  /** Asker name (or email, if name-less) — decrypted on the authorized staff-console queue path so
   *  staff can see + search by asker. `null` when the asker was anonymous. Never in the export. */
  reporterName: string | null;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Staff-side full detail for a question (mirrors StaffRequestDetail, question-shaped). */
export interface StaffQuestionDetail {
  id: string;
  trackingCode: string;
  topic: string;
  topicLabel?: string;
  question: string;
  status: QuestionStatus;
  answer: string;
  answeredAt: string | null;
  faqId: string | null;
  reporterContact: ReporterContactView | null;
  assigneeId: string | null;
  events: AuditEventView[];
  createdAt: string;
  updatedAt: string;
}

/** The public, PII-scrubbed FAQ entry (shared.FAQ mirror). */
export interface FAQView {
  id: string;
  topicId: string;
  topicLabel?: string;
  question: string;
  answerHtml: string;
  published: boolean;
  featured: boolean;
  order: number;
  viewCount: number;
  helpfulYes: number;
  helpfulNo: number;
  sourceQuestionIds?: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface PublishFAQInput {
  /** Source question to publish FROM (the deflection loop). Omit to author a STANDALONE FAQ. */
  questionId?: string;
  mergeQuestionIds?: string[];
  topicId: string;
  question: string;
  answerHtml: string;
  featured?: boolean;
  order?: number;
}

export interface UpdateFAQInput {
  topicId: string;
  question: string;
  answerHtml: string;
  published: boolean;
  featured: boolean;
  order: number;
}

export interface SuggestFAQDraft {
  sourceRequestId: string;
  draft: { topicId: string; question: string; answerHtml: string };
}

export interface FAQAnalytics {
  mostViewed: Array<{ faqId: string; question: string; viewCount: number }>;
  mostAskedTopics: Array<{ topicLabel: string; count: number }>;
  helpfulRatioPct: number | null;
  /** Documented proxy signal (typeahead-shown event is Phase 3, not yet built) — never presented
   *  as an exact measurement. */
  deflectionRatePct: number | null;
}

/** AI FAQ-answer "Clean up" helper — mode (POST /api/admin/ai/cleanup). */
export type AiCleanupMode = "cleanup" | "plain";

// ---- Open Records (KORA) requests (docs/features/records-request.md) -------------------------
//
// KindKORA — a NEW citizen intake Kind on the shared intake→track→notify engine (♻️ mirrors how
// "question" was added alongside "concern"). MVP tier only: log → track → respond → audit. State-
// configurable (respondDays/citationLabel are per-tenant TenantSettings, ship the Kansas KORA
// profile as the v1 default — see embedConfigMap.ts `kora` block). KORA has its OWN disjoint
// status value-space (shared/status.go KORAStatuses) — distinct strings from RequestStatus AND
// QuestionStatus so the values never collide when read side-by-side in the console.

/** Records-request lifecycle (shared.KORAStatuses mirror) — distinct from RequestStatus/QuestionStatus. */
export type KORAStatus =
  | "KReceived"
  | "KAcknowledged"
  | "KInProgress"
  | "KFulfilled"
  | "KPartiallyFulfilled"
  | "KDenied"
  | "KClosed";

export const KORA_STATUS_LABELS: Record<KORAStatus, string> = {
  KReceived: "Received",
  KAcknowledged: "Acknowledged",
  KInProgress: "In Progress",
  KFulfilled: "Fulfilled",
  KPartiallyFulfilled: "Partially Fulfilled",
  KDenied: "Denied",
  KClosed: "Closed",
};

/** Terminal KORA statuses (shared.IsKORATerminal mirror) — no outbound transitions except Closed. */
export const KORA_TERMINAL_STATUSES: readonly KORAStatus[] = [
  "KFulfilled",
  "KPartiallyFulfilled",
  "KDenied",
  "KClosed",
];

export function isKORATerminal(status: KORAStatus): boolean {
  return KORA_TERMINAL_STATUSES.includes(status);
}

/** Allowed KORA status transitions (shared.koraAllowedTransitions mirror) — drives the Respond
 *  action's status select so staff can never submit an illegal transition client-side (the server
 *  is still the source of truth / re-validates, ♻️ same posture as concern's canTransition). */
const KORA_TRANSITIONS: Record<KORAStatus, readonly KORAStatus[]> = {
  KReceived: ["KAcknowledged", "KInProgress", "KFulfilled", "KPartiallyFulfilled", "KDenied"],
  KAcknowledged: ["KInProgress", "KFulfilled", "KPartiallyFulfilled", "KDenied"],
  KInProgress: ["KFulfilled", "KPartiallyFulfilled", "KDenied"],
  KFulfilled: ["KClosed"],
  KPartiallyFulfilled: ["KClosed"],
  KDenied: ["KClosed"],
  KClosed: [],
};

export function canTransitionKORA(from: KORAStatus, to: KORAStatus): boolean {
  if (from === to) return false;
  return KORA_TRANSITIONS[from].includes(to);
}

/** "inspect" (view in person) | "copies" (paper/printed) | "electronic" (digital delivery) — the
 *  requester's preferred fulfillment format (records-request.md). */
export type RecordsPreferredFormat = "inspect" | "copies" | "electronic";

/** Compact row for the staff KORA console queue (♻️ QuestionQueueRow shape, recordType/dueDate
 *  instead of topic/SLA-stage — KORA has its own single-stage response clock, not the concern
 *  two-stage ack/resolve SLA). */
export interface KORAQueueRow {
  id: string;
  trackingCode: string;
  recordsSought: string;
  recordType?: string;
  recordTypeLabel?: string;
  status: KORAStatus;
  preferredFormat: RecordsPreferredFormat;
  /** Requester name (or email, if name-less) — decrypted on the authorized staff-console queue
   *  path so staff can see + search by requester. `null` when the request was anonymous. Never in
   *  the CSV export (records-request.md "open-records-clean"). */
  requesterName: string | null;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Business days since CreatedAt (single-stage response clock, records-request.md). */
  ageBusinessDays: number;
  /** True when ageBusinessDays > the tenant's respondDays target and status is not terminal. */
  overdue: boolean;
  /** The business-day target that applies (TenantSettings.KORAConfig.RespondDays). */
  targetDays: number;
}

/** Staff-side full detail for a KORA request (mirrors StaffRequestDetail/StaffQuestionDetail,
 *  records-request-shaped). Requester contact reuses the encrypted ReporterContact path — KORA
 *  allows anonymous requests, so contact is OPTIONAL. */
export interface StaffKORADetail {
  id: string;
  trackingCode: string;
  recordsSought: string;
  recordType?: string;
  recordTypeLabel?: string;
  dateFrom?: string;
  dateTo?: string;
  preferredFormat: RecordsPreferredFormat;
  status: KORAStatus;
  reporterContact: ReporterContactView | null;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: AuditEventView[];
  createdAt: string;
  updatedAt: string;
  // ---- response fields (set on Respond) ----
  denyReason?: string;
  redactionNote?: string;
  fulfilledFormat?: RecordsPreferredFormat;
  respondedAt?: string;
  /** Business days since CreatedAt / the response-clock target (same posture as KORAQueueRow). */
  ageBusinessDays: number;
  overdue: boolean;
  targetDays: number;
}

/** POST /public/records-requests body (SubmitKORAInput mirror). Tenant is ALWAYS server-resolved —
 *  never accepted here. `dateFrom`/`dateTo` are optional ISO dates bounding the records sought;
 *  `requesterName`/`requesterContact` are optional (anonymous KORA requests are allowed). */
export interface SubmitKORAInput {
  recordsSought: string;
  dateFrom?: string;
  dateTo?: string;
  preferredFormat: RecordsPreferredFormat;
  recordType?: string;
  requesterName?: string;
  requesterContact?: { name?: string; email?: string; phone?: string };
  attachments?: string[];
}

/** POST /admin/kora/{id}/respond body (RespondKORAInput mirror). `denyReason` is REQUIRED
 *  non-empty when status=KDenied; `publicNote` is required on a terminal
 *  fulfilled/partial/denied transition (validate.go ValidateRespondKORA) — the UI enforces the
 *  same rule client-side so the error surfaces before the round trip, not after. */
export interface RespondKORAInput {
  status: KORAStatus;
  publicNote?: string;
  denyReason?: string;
  redactionNote?: string;
  fulfilledFormat?: RecordsPreferredFormat;
}

// ---- Code Enforcement (Case) — a FOURTH intake Kind (services/api/internal/handler/case.go) ---
//
// A Case is a Request with Kind="case" (♻️ mirrors how KORA was added alongside concern/question).
// STAFF-ONLY: an officer opens a case; there is no public/resident intake surface for one at all.
// Case has its OWN disjoint status value-space (shared/status.go CaseStatuses, all prefixed "CE"),
// distinct from RequestStatus/QuestionStatus/KORAStatus. This is a document->notice->track->
// compliance->audit tool, NOT a statutory-compliance engine — OrdinanceRef/CureSteps are staff-
// entered free text, never a hardcoded ordinance citation or fine schedule.

/** Case lifecycle (shared.CaseStatuses mirror) — distinct from every other Status value-space. */
export type CaseStatus =
  "CEOpen" | "CENotice" | "CEReinspected" | "CEResolved" | "CEEscalated" | "CEClosed";

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  CEOpen: "Open",
  CENotice: "Notice Issued",
  CEReinspected: "Re-inspected",
  CEResolved: "Resolved",
  CEEscalated: "Escalated",
  CEClosed: "Closed",
};

/** Terminal case statuses (shared.IsCaseTerminal mirror) — no outbound transitions except Closed. */
export const CASE_TERMINAL_STATUSES: readonly CaseStatus[] = [
  "CEResolved",
  "CEEscalated",
  "CEClosed",
];

export function isCaseTerminal(status: CaseStatus): boolean {
  return CASE_TERMINAL_STATUSES.includes(status);
}

/** Allowed case status transitions (shared.caseAllowedTransitions mirror) — drives the case
 *  lifecycle actions so staff can never submit an illegal transition client-side (the server is
 *  still the source of truth / re-validates, ♻️ same posture as canTransitionKORA). */
const CASE_TRANSITIONS: Record<CaseStatus, readonly CaseStatus[]> = {
  CEOpen: ["CENotice", "CEResolved", "CEClosed"],
  CENotice: ["CEReinspected", "CEResolved", "CEEscalated"],
  CEReinspected: ["CEResolved", "CEEscalated", "CENotice"],
  CEResolved: ["CEClosed"],
  CEEscalated: ["CEClosed"],
  CEClosed: [],
};

export function canTransitionCase(from: CaseStatus, to: CaseStatus): boolean {
  if (from === to) return false;
  return CASE_TRANSITIONS[from].includes(to);
}

/** "compliant" | "not_compliant" — required on the transition to CEReinspected
 *  (shared.CaseReinspectionOutcomes mirror). */
export type CaseReinspectionOutcome = "compliant" | "not_compliant";

/** Compact row for the staff case console queue (handler.ListCaseQueueHandler — ♻️ KORAQueueRow
 *  shape, compliance-deadline overdue flag instead of the KORA response clock). */
export interface CaseQueueRow {
  id: string;
  trackingCode: string;
  violationType?: string;
  violationTypeLabel?: string;
  description: string;
  status: CaseStatus;
  ownerName?: string;
  propertyId?: string | null;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
  complianceDeadline?: string;
  /** True when the case is past ComplianceDeadline and status is not terminal
   *  (shared.EvaluateComplianceDeadline). */
  overdue: boolean;
  /** Days past ComplianceDeadline (0 when not overdue). */
  daysOverdue: number;
}

/** Staff-side full detail for a case (mirrors StaffKORADetail, case-shaped). No reporterContact —
 *  a case is officer-opened; the enforcement subject is OwnerName/OwnerAddress, not PII (case.go
 *  doc: "public property-owner-of-record information, defensibly on the open record"). */
export interface StaffCaseDetail {
  id: string;
  trackingCode: string;
  violationType?: string;
  violationTypeLabel?: string;
  description: string;
  status: CaseStatus;
  ordinanceRef?: string;
  ownerName?: string;
  ownerAddress?: string;
  complianceDeadline?: string;
  noticeIssuedAt?: string;
  cureSteps?: string;
  reinspectionOutcome?: CaseReinspectionOutcome | "";
  sourceRequestId?: string;
  propertyId: string | null;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: AuditEventView[];
  createdAt: string;
  updatedAt: string;
  /** Calendar-date compliance-deadline countdown/overdue flag (shared.EvaluateComplianceDeadline —
   *  NOT the concern/KORA business-day SLA model; a case's clock is the notice's explicit deadline
   *  date). */
  overdue: boolean;
  daysOverdue: number;
}

/** POST /api/admin/cases body (OpenCaseInput mirror). `sourceRequestId` is the concern (Kind=concern)
 *  this case escalates from — empty/omitted for a proactive (non-escalated) case. `propertyId` is
 *  optional — when omitted and sourceRequestId is set, the server inherits the source concern's
 *  property automatically. */
export interface OpenCaseInput {
  category: string;
  description: string;
  ordinanceRef?: string;
  ownerName?: string;
  ownerAddress?: string;
  propertyId?: string;
  sourceRequestId?: string;
}

export interface OpenCaseResult {
  trackingCode: string;
  requestId: string;
  status: CaseStatus;
  sourceRequestId?: string;
}

/** POST /api/admin/cases/{id}/advance body (AdvanceCaseInput mirror). `complianceDeadline`
 *  ("YYYY-MM-DD") + `cureSteps` are REQUIRED when status=="CENotice" (issuing a notice);
 *  `reinspectionOutcome` is REQUIRED when status=="CEReinspected" — the UI enforces the same rule
 *  client-side so the error surfaces before the round trip, not after (validate.go
 *  ValidateAdvanceCase mirror). */
export interface AdvanceCaseInput {
  status: CaseStatus;
  note?: string;
  complianceDeadline?: string;
  cureSteps?: string;
  ordinanceRef?: string;
  ownerName?: string;
  ownerAddress?: string;
  reinspectionOutcome?: CaseReinspectionOutcome;
}

// ---- Permits & Licensing (roadmap module 6) — a SIXTH intake Kind (services/api/internal/handler/permit.go) ---
//
// A permit application is a Request with Kind="permit" (♻️ mirrors how KORA/Case were added). This
// COMBINES a public application intake (like KORA) with a staff review->approve/deny->inspect->issue
// workflow (like Case). Permit has its OWN disjoint status value-space (shared/status.go
// PermitStatuses, all prefixed "P"), distinct from every other Status value-space. MVP uses ONE
// flexible application form — per-type dynamic forms, fee-estimate-at-apply, online payment,
// renewals, multi-approver routing, inspection checklists, conditions/holds/stop-work are all
// DEFERRED (task brief). Conditions/DenyReason are staff-authored free text and the issued-permit
// document is a per-town EDITABLE template — never hardcoded ordinance/fee law. Fees are TRACKED
// only; this MVP never collects payment online.

/** Permit lifecycle (shared.PermitStatuses mirror) — distinct from every other Status value-space. */
export type PermitStatus =
  "PSubmitted" | "PUnderReview" | "PApproved" | "PDenied" | "PInspections" | "PIssued" | "PClosed";

export const PERMIT_STATUS_LABELS: Record<PermitStatus, string> = {
  PSubmitted: "Submitted",
  PUnderReview: "Under Review",
  PApproved: "Approved",
  PDenied: "Denied",
  PInspections: "Inspections",
  PIssued: "Issued",
  PClosed: "Closed",
};

/** Terminal permit statuses (shared.IsPermitTerminal mirror) — terminal = PClosed ONLY; PDenied →
 *  PClosed is its only edge, so a denied permit is not itself a dead end. */
export const PERMIT_TERMINAL_STATUSES: readonly PermitStatus[] = ["PClosed"];

export function isPermitTerminal(status: PermitStatus): boolean {
  return PERMIT_TERMINAL_STATUSES.includes(status);
}

/** Allowed permit status transitions (shared.permitAllowedTransitions mirror) — drives the permit
 *  lifecycle actions so staff can never submit an illegal transition client-side (the server is
 *  still the source of truth / re-validates, ♻️ same posture as canTransitionCase). */
const PERMIT_TRANSITIONS: Record<PermitStatus, readonly PermitStatus[]> = {
  PSubmitted: ["PUnderReview", "PDenied"],
  PUnderReview: ["PApproved", "PDenied"],
  PApproved: ["PInspections", "PIssued", "PDenied"],
  PInspections: ["PIssued", "PDenied"],
  PIssued: ["PClosed"],
  PDenied: ["PClosed"],
  PClosed: [],
};

export function canTransitionPermit(from: PermitStatus, to: PermitStatus): boolean {
  if (from === to) return false;
  return PERMIT_TRANSITIONS[from].includes(to);
}

/** "pass" | "fail" — a recorded inspection outcome (shared.InspectionResultPass/Fail mirror). */
export type InspectionResult = "pass" | "fail";

// ---- Permits fidelity (Phase A) — form-schema engine (docs/features/permits-fidelity.md §A) -------
//
// FormSchema attaches to a Kind=permit Category row (shared.Category.FormSchema mirror); FormValues
// is the applicant's flat string-keyed answer map on the Request (shared.Request.FormValues mirror).
// Both are nil/empty for a legacy (pre-fidelity) permit type/application — the apply page falls back
// to the flexible free-text form (ledger AC A.2) and the staff detail page simply renders no
// "Application detail" section.

/** shared.FormFieldType mirror — the field-type switch SchemaFormRenderer dispatches on. */
export type FormFieldType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "checkbox"
  | "checkbox-group"
  | "date"
  | "dimensions"
  | "section-note";

export interface FormFieldOption {
  value: string;
  label: string;
}

/** One-level conditional-show rule (shared.FormFieldShowWhen mirror) — no nested boolean trees. */
export interface FormFieldShowWhen {
  field: string;
  equals: string;
}

/** One input (or static note) within a FormSection (shared.FormField mirror). `key` is the STABLE
 *  machine key FormValues answers are stored under — never renumbered. */
export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  options?: FormFieldOption[];
  help?: string;
  /** Display + PDF suffix, e.g. "amps", "gal", "sq ft", "$". */
  unit?: string;
  showWhen?: FormFieldShowWhen;
  /** Bounds a text/textarea answer; absent/0 means "use the shared default". */
  maxLen?: number;
}

/** One collapsible section of a FormSchema (shared.FormSection mirror). */
export interface FormSection {
  key: string;
  title: string;
  help?: string;
  fields: FormField[];
}

/** The versioned, per-permit-type form definition (Category.FormSchema, shared.FormSchema mirror).
 *  `version` is bumped on any field-shape change; a stored FormValues answer set records the version
 *  it was captured under (StaffPermitDetail.formSchemaVersion) so a later schema edit never
 *  mis-renders an old answer set. */
export interface FormSchema {
  version: number;
  sections: FormSection[];
}

/** shared.RequiredDocKind mirror — classifies a Category.RequiredDocs entry (ledger §A.4). */
export type RequiredDocKind = "upload" | "affidavit-ack" | "external-prereq";

/** One prerequisite item on the per-type required-docs checklist — WARNS, does not block submit,
 *  EXCEPT the affidavit-ack kind (ledger §A.4 exception: the applicant must tick the box to submit). */
export interface RequiredDoc {
  key: string;
  label: string;
  help?: string;
  kind: RequiredDocKind;
}

/** The applicant's schema-driven answers (shared.FormValues mirror) — ONE flat string-keyed map, NOT
 *  40 typed columns. Every value is a STRING on the wire: checkbox="true"/"false", checkbox-group=
 *  comma-joined option values, dimensions="10x12" (or "10x12x8" with height). */
export type FormValues = Record<string, string>;

/** Compact row for the staff permit console queue (handler.ListPermitQueueHandler — ♻️ KORAQueueRow
 *  shape, permitTypeLabel/feeAmount/feePaid instead of recordType/response-clock — a permit has no
 *  SLA clock in MVP). */
export interface PermitQueueRow {
  id: string;
  trackingCode: string;
  permitType?: string;
  permitTypeLabel?: string;
  projectDescription: string;
  address?: string;
  status: PermitStatus;
  /** Applicant name (or email, if name-less) — decrypted on the authorized staff-console queue path
   *  so staff can see + search by applicant. `null` when the applicant left no contact. Never in the
   *  export. */
  applicantName: string | null;
  /** Fee model (permits-fidelity.md §A.5, three tracked amounts): `totalFeeAmount` is the
   *  rename-with-alias replacement for the shipped `feeAmount` — a legacy prod permit's on-disk
   *  `feeAmount` attribute reads correctly as `totalFeeAmount` (no data migration). `appFeeAmount`/
   *  `impactFeeAmount` are net-new — 0 on every legacy permit. */
  totalFeeAmount: number;
  appFeeAmount: number;
  impactFeeAmount: number;
  feePaid: boolean;
  assigneeId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Staff-side full detail for a permit application (mirrors StaffCaseDetail/StaffKORADetail,
 *  permit-shaped). `formSchema`/`formValues` are present when the permit's type carries a
 *  Phase-A schema (undefined/empty for a legacy free-text-only application). */
export interface StaffPermitDetail {
  id: string;
  trackingCode: string;
  permitType?: string;
  permitTypeLabel?: string;
  projectDescription: string;
  location: { address: string; lat?: number; lng?: number };
  status: PermitStatus;
  reporterContact: ReporterContactView | null;
  conditions?: string;
  denyReason?: string;
  totalFeeAmount: number;
  appFeeAmount: number;
  impactFeeAmount: number;
  feePaid: boolean;
  permitNumber?: string;
  issuedAt?: string;
  assigneeId: string | null;
  attachments: AttachmentView[];
  events: AuditEventView[];
  createdAt: string;
  updatedAt: string;
  /** The permit-type's FormSchema, as captured at the time this application was hydrated (read from
   *  the live category taxonomy — always the CURRENT schema, paired with formSchemaVersion below to
   *  detect when an answer set predates a schema edit). Undefined for a legacy/free-text type. */
  formSchema?: FormSchema;
  /** The applicant's schema-driven answers. Undefined/empty for a legacy free-text application. */
  formValues?: FormValues;
  /** The FormSchema.version the answers were captured under (shared.Request.FormSchemaVersion mirror). */
  formSchemaVersion?: number;
  /** The permit-type's required-docs checklist (Category.RequiredDocs), read alongside formSchema —
   *  drives the staff-side Prerequisites met/unmet summary. Undefined for a legacy/free-text type. */
  requiredDocs?: RequiredDoc[];
  /** Structured Inspections (permit-inspections.md Phase 1) — the permit's own recorded Inspection
   *  rows, its type's expected checklist, and the derived progress. All three are populated by the
   *  SAME GET /api/requests/{id} response when this request is a permit (handler.GetRequest) —
   *  undefined/empty `inspectionTypes` means "no checklist" (e.g. a Water permit), which is a fully
   *  valid state, not a loading/error state. */
  inspections?: Inspection[];
  inspectionTypes?: InspectionTypeDef[];
  inspectionProgress?: InspectionProgressSummary;
}

/** POST /public/permits body (SubmitPermitInput mirror). Tenant/Kind/status are ALL server-set,
 *  never accepted here. `applicantName`/`applicantContact` are optional-but-expected (unlike KORA, a
 *  permit applicant is normally known). `formValues` are the applicant's schema-driven answers for
 *  the selected type's FormSchema — validated SERVER-SIDE (permits-fidelity.md §A.2); omitted/empty
 *  is valid for a permit type with no FormSchema (legacy free-text application). */
export interface SubmitPermitInput {
  permitType: string;
  projectDescription: string;
  location: { address: string; lat?: number; lng?: number };
  applicantName?: string;
  applicantContact?: { name?: string; email?: string; phone?: string };
  attachments?: string[];
  formValues?: FormValues;
}

/** POST /admin/permits/{id}/advance body (AdvancePermitInput mirror). `denyReason` is REQUIRED
 *  non-empty when status=PDenied; `permitNumber` is REQUIRED when status=PIssued — the UI enforces
 *  the same rule client-side so the error surfaces before the round trip, not after (validate.go
 *  ValidateAdvancePermit mirror). Fee model (permits-fidelity.md §A.5): `totalFeeAmount` is REQUIRED
 *  when status=PApproved (the Building Codes Officer sets the variable total); `impactFeeAmount` is
 *  staff-settable/overridable (auto-derived server-side from FormValues when omitted);
 *  `appFeeAmount` is intentionally NOT settable here — stamped once at submit, read-only thereafter. */
export interface AdvancePermitInput {
  status: PermitStatus;
  note?: string;
  conditions?: string;
  denyReason?: string;
  totalFeeAmount?: number;
  impactFeeAmount?: number;
  feePaid?: boolean;
  permitNumber?: string;
}

/** POST /admin/permits/{id}/inspection body (RecordInspectionInput mirror). APPENDS a
 *  public-visibility inspection event to the permit's timeline — does NOT itself change the
 *  permit's status. `typeKey`, when set, MUST match one of the permit type's InspectionTypeDef
 *  keys (a checklist slot); otherwise `label` is required (a free-form, off-list inspection) —
 *  validate.go ValidateRecordInspection mirror, enforced client-side before submit too. */
export interface RecordInspectionInput {
  result: InspectionResult;
  date: string;
  note?: string;
  typeKey?: string;
  label?: string;
}

// ---- Structured Inspections (docs/features/permit-inspections.md, Phase 1) -------------------
//
// A permit TYPE carries an ORDERED list of expected inspections (the checklist — e.g. Building:
// footing, framing, rough-in, final); a type with none (e.g. Water) simply has no checklist.
// Sibling to FormSchema/RequiredDocs on Category (shared.Category.InspectionTypes mirror). An
// Inspection is a first-class per-permit facet row; checklist progress is DERIVED at read time
// from the type's expected list + the permit's recorded Inspections — never stored.

/** One expected-inspection slot on a permit type's checklist (shared.InspectionTypeDef mirror).
 *  `key` is the STABLE machine key an actual Inspection's `typeKey` references — never renumbered,
 *  same discipline as FormField.key. `label` is the human display name. */
export interface InspectionTypeDef {
  key: string;
  label: string;
}

/** "requested" | "scheduled" | "passed" | "failed" | "declined" (shared.InspectionStatus mirror).
 *  Phase 1 only ever WRITES/SHOWS passed/failed — requested/scheduled/declined are Phase 2
 *  (resident-requested inspections); render them gracefully if present (e.g. a future live read)
 *  but no Phase 1 UI builds a flow for them. */
export type InspectionStatus = "requested" | "scheduled" | "passed" | "failed" | "declined";

/** A first-class recorded inspection (shared.Inspection mirror) — a child of the permit Request.
 *  `typeKey` set = a checklist slot; empty + `label` set = a free-form, off-list inspection. */
export interface Inspection {
  id: string;
  typeKey?: string;
  label?: string;
  status: InspectionStatus;
  requestedDate?: string;
  scheduledDate?: string;
  result?: string;
  note?: string;
  requestedBy: "staff" | "resident";
  createdAt: string;
  updatedAt: string;
}

/** One checklist row's derived state (shared.InspectionSlotProgress mirror) — one per expected
 *  InspectionTypeDef (in the type's defined order), PLUS one extra row per free-form (off-checklist)
 *  Inspection. `latest` is the most recently created Inspection for this slot (so a fail-then-pass
 *  on the same slot reads as the pass) — undefined if nothing has ever been recorded for this slot. */
export interface InspectionSlotProgress {
  typeKey?: string;
  label: string;
  latest?: Inspection;
}

/** GET /api/requests/{rid} (staff permit detail) `inspectionProgress` (shared.InspectionProgressSummary
 *  mirror) — ALWAYS derived server-side, never stored. `total` = the type's expected-slot count (a
 *  free-form inspection never counts toward "N of M", since it was never expected). */
export interface InspectionProgressSummary {
  slots: InspectionSlotProgress[];
  passed: number;
  total: number;
}

// ---- Permit → POA daily digest — "Log a paper permit" (docs/features/permit-poa-digest.md) ------
//
// Staff-counter/scanned-paper permit intake: pick a type, upload one scan, Claude OCR pre-fills the
// SAME SchemaFormRenderer the resident apply page uses, low-confidence fields get a "double-check"
// flag the clerk clears by editing (or the field stays flagged), then the clerk picks the initial
// status (their call — a scanned permit may already be approved) and saves via the staff-create
// endpoint. OCR is a separate, explicit, opt-in AI action (never automatic) — same posture as
// draftAgenda/summarizeAgendaItem.

/** POST /api/admin/permits/ocr body — one scanned/photographed permit file, read with AI into a
 *  flat FormValues-shaped answer map for the given type's FormSchema (server drops unknown keys). */
export interface OcrPermitFieldsInput {
  permitType: string;
  contentType: string;
  /** Base64-encoded file bytes, WITHOUT the `data:...;base64,` prefix. */
  dataBase64: string;
}

/** POST /api/admin/permits/ocr response — `values` are pre-fill candidates for SchemaFormRenderer
 *  (string-keyed, same shape as FormValues); `confidence` is a PARALLEL map (same keys) with each
 *  value's 0–1 confidence score, used client-side to flag a field for "double-check" below the
 *  LOG_PAPER_PERMIT_CONFIDENCE_THRESHOLD. A key present in `values` but absent from `confidence` is
 *  treated as high-confidence (never flagged) — the server only includes a confidence entry when it
 *  has a real signal to report. */
export interface OcrPermitFieldsResult {
  permitType: string;
  values: FormValues;
  confidence: Record<string, number>;
}

/** Confidence threshold below which a field is flagged "Double-check" (permit-poa-digest.md "Scan
 *  → OCR pre-fill" §UX) — mirrors the approved mockup's "e.g. 0.75" annotation. */
export const LOG_PAPER_PERMIT_CONFIDENCE_THRESHOLD = 0.75;

/** POST /api/admin/permits body (CreatePermitByStaffInput mirror) — staff-counter/paper/scanned
 *  intake. UNLIKE SubmitPermitInput (always starts PSubmitted), `status` is the clerk-CHOSEN initial
 *  status — e.g. logging a permit the town already approved on paper before TownDesk existed. */
export interface CreatePermitByStaffInput {
  permitType: string;
  status: PermitStatus;
  projectDescription: string;
  location: { address: string; lat?: number; lng?: number };
  applicantContact?: ReporterContactView;
  formValues?: FormValues;
}

/** POST /api/admin/permits response (handler.CreatePermitByStaff mirror) — a thin ack, not the full
 *  permit; callers re-fetch via getPermit(id) to land on the real detail page (same "mutation
 *  returns an ack, caller re-GETs" convention as every other staff mutation in this client). */
export interface CreatePermitByStaffResult {
  id: string;
  trackingCode: string;
  status: PermitStatus;
  createdAt: string;
}

/** POST /api/admin/subscribers body (AddSubscriberByStaffInput mirror) — single-opt-in: the staff
 *  action itself is the consent record, confirmed immediately, no confirmation email. Omitted/empty
 *  `channels` means "subscribed to everything" — the same load-bearing nil-means-all semantics as
 *  the public SubscribeInput.Channels. */
export interface AddSubscriberByStaffInput {
  email: string;
  name?: string;
  channels?: string[];
}

// ---- Building & Code Enforcement Council Report (docs/features/council-report.md) -------------
//
// GET /api/admin/reports/council?scope=permits|cases|combined&from=&to=[&format=pdf] — a
// staff-triggered, READ-ONLY, scope-selectable activity report over the LIVE Permits (Kind=permit)
// and/or Code Enforcement (Kind=case) data for a date range. Every count/list/fee-sum/aging figure
// is COMPUTED server-side (handler.GetCouncilReport / shared.BuildCouncilReportFigures) — the AI
// narrative is prose-only, never a number (AC2/AC7). Mirrors the Go DTOs
// (internal/handler/council_report.go CouncilReportResult, internal/shared/council_report.go
// CouncilReportFigures) EXACTLY — no invented field names (the KORA-drift lesson).

/** Report scope — a FILTER, not a fork (council-report.md AC1). */
export type CouncilReportScope = "permits" | "cases" | "combined";

export const COUNCIL_REPORT_SCOPE_LABELS: Record<CouncilReportScope, string> = {
  permits: "Permits (Building)",
  cases: "Cases (Codes)",
  combined: "Combined",
};

export interface CouncilReportPeriod {
  from: string;
  to: string;
  generatedAt: string;
}

export interface NewPermitApplication {
  type: string;
  address: string;
  date: string;
}

export interface PermitFigures {
  newApplications: number;
  newApplicationsList: NewPermitApplication[];
  approved: number;
  issued: number;
  denied: number;
  /** true = the counts above are in-period transitions; false = a labelled current-status fallback
   *  (council-report.md's "In-period vs at-period-end" note — never a wrong number). */
  transitionsFromEvents: boolean;
  inspectionsPerformed: number;
  inspectionsPassed: number;
  inspectionsFailed: number;
  openAtPeriodEnd: number;
  overdueOpen: number;
  /** false = the permit model exposes no deadline field today — overdueOpen is structurally 0 and
   *  the UI must omit the "overdue" line rather than imply a real zero. */
  overdueTracked: boolean;
  feesTrackedTotal: number;
  feesTrackedLabel: string;
}

export interface NewCaseOpened {
  violationType: string;
  address: string;
  date: string;
}

export interface OverdueCase {
  violationType: string;
  address: string;
  daysOverdue: number;
}

export interface CaseFigures {
  newOpened: number;
  newOpenedList: NewCaseOpened[];
  noticesIssued: number;
  reinspected: number;
  resolved: number;
  escalated: number;
  closed: number;
  transitionsFromEvents: boolean;
  openAtPeriodEnd: number;
  overdue: number;
  overdueList: OverdueCase[];
}

/** The AGENDA SEAM (council-report.md classDiagram) — Permits/Cases are optional so a single-scope
 *  report's JSON omits the section that doesn't apply. */
export interface CouncilReportFigures {
  scope: CouncilReportScope;
  title: string;
  period: CouncilReportPeriod;
  permits?: PermitFigures;
  cases?: CaseFigures;
}

/** GET /api/admin/reports/council response shape (handler.CouncilReportResult mirror). */
export interface CouncilReportResult {
  html: string;
  figures: CouncilReportFigures;
}

// ---- Council Agenda + Packet (agenda-packet.md, Phase 3/3b/4) --------------------------------
//
// Meeting/AgendaItem/MeetingAttachment/Contribution/Minutes are their OWN top-level aggregate — NOT
// a Request Kind (see the ledger §3). These view types mirror the Go wire shapes in
// services/api/internal/shared/schemas.go + the ALLOWLIST projections in meetings_public.go/
// contributor.go 1:1 (field names/optionality) — no drift.

export type MeetingStatus = "draft" | "published" | "archived";

/** Meeting.AgendaState (agenda-packet.md §10A tentative/final) — "" (pre-publish / legacy rows)
 *  reads as no state yet, never rendered as a banner. A DRAFT publishes as "tentative" by default;
 *  "final" is the clerk's explicit "Mark final" action on an already-published meeting. */
export type AgendaState = "" | "tentative" | "final";

export interface VoteTally {
  ayes: number;
  nays: number;
  abstain: number;
  detail?: string;
}

/** MeetingTemplate — a named, reusable agenda skeleton + default meeting metadata (ADR-0021,
 *  docs/features/meeting-templates.md). Mirrors shared.MeetingTemplate 1:1. Tenant-scoped;
 *  never public, never appears in the meetings list or any published output. */
export interface MeetingTemplateView {
  id: string;
  tenantId: string;
  /** The template's OWN label ("Departmental Reports", "RNR Hearing") — distinct from `title`,
   *  which is the default MEETING title materialize seeds. */
  name: string;
  body?: string;
  title?: string;
  location?: string;
  startTime?: string;
  sections: MeetingTemplateSectionView[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** A top-level section — maps to a top-level AgendaItem on materialize. */
export interface MeetingTemplateSectionView {
  title: string;
  narrativeHtml?: string;
  presenter?: string;
  /** Department keys -> Contribution slots (state "requested") on materialize. */
  departments?: string[];
  /** OPTIONAL staff-only pacing estimate (minutes), carried to the materialized top-level
   *  AgendaItem — 0/absent = unset. See AgendaItemView.timeboxMinutes. */
  timeboxMinutes?: number;
  items: MeetingTemplateItemView[];
}

/** A sub-item — maps to a child AgendaItem on materialize. TWO-LEVEL NESTING ONLY (mirrors
 *  AgendaItemView's own constraint) — a MeetingTemplateItemView never itself carries sub-items. */
export interface MeetingTemplateItemView {
  title: string;
  narrativeHtml?: string;
  presenter?: string;
  /** OPTIONAL staff-only pacing estimate (minutes), carried to the materialized child
   *  AgendaItem — 0/absent = unset. See AgendaItemView.timeboxMinutes. */
  timeboxMinutes?: number;
}

/** POST /api/admin/meeting-templates body. `name` required; every other field optional (an empty
 *  `sections` is a valid, if useless, template). */
export interface CreateMeetingTemplateInput {
  name: string;
  body?: string;
  title?: string;
  location?: string;
  startTime?: string;
  sections?: MeetingTemplateSectionView[];
}

/** PUT /api/admin/meeting-templates/{templateId} body — a FULL REPLACE of the template's editable
 *  fields, same shape as create. */
export type UpdateMeetingTemplateInput = CreateMeetingTemplateInput;

/** POST /api/admin/meeting-templates/{templateId}/materialize body. `meetingDate` is REQUIRED (the
 *  one thing a template never carries — the clerk supplies it); `startTime`/`location` are
 *  OPTIONAL overrides of the template's own defaults. */
export interface MaterializeTemplateInput {
  meetingDate: string; // ISO "YYYY-MM-DD"
  startTime?: string;
  location?: string;
}

/** POST /api/admin/meetings/{id}/save-as-template body — `name` is the only field this endpoint
 *  accepts; every other template field is projected server-side from the source meeting. */
export interface SaveAsTemplateInput {
  name: string;
}

export interface MeetingView {
  id: string;
  tenantId: string;
  body: string;
  title?: string;
  meetingDate: string; // ISO "YYYY-MM-DD"
  startTime?: string;
  location: string;
  status: MeetingStatus;
  agendaState?: AgendaState;
  publishedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/admin/meetings?pageSize=&cursor= response shape (handler.Meetings, now PAGINATED —
 *  a breaking change from the prior bare-array response). Always newest-meeting-first from the
 *  server. An empty `nextCursor` means the client has reached the last page; a non-empty one is an
 *  OPAQUE string to round-trip back as the next request's `cursor` — never parsed/constructed
 *  client-side. */
export interface MeetingsListPage {
  meetings: MeetingView[];
  nextCursor: string;
}

/** Default/max `pageSize` for GET /api/admin/meetings — mirrors
 *  shared.MeetingsListDefaultPageSize/MeetingsListMaxPageSize server-side. */
export const MEETINGS_LIST_DEFAULT_PAGE_SIZE = 25;
export const MEETINGS_LIST_MAX_PAGE_SIZE = 100;

/** "accessible" | "not_verified" — MeetingAttachment.Accessible (staff self-declared, never
 *  auto-verified — agenda-packet.md §0.1/§7). */
export type MeetingAttachmentAccessible = "accessible" | "not_verified";

/** ParentItemID — "" = top-level (a "section"); otherwise the id of the top-level item this is a
 *  first-class SUB-ITEM of (Council Agenda 10A nested sub-items — agenda-packet.md nested §, backend
 *  #221). TWO-LEVEL NESTING ONLY: a sub-item's own parentItemId may never itself point at another
 *  sub-item — server-enforced, never trust the client. Absent/"" reads as top-level (backward
 *  compat for every pre-nesting row). */
export interface AgendaItemView {
  id: string;
  tenantId: string;
  meetingId: string;
  order: number;
  parentItemId?: string;
  title: string;
  narrativeHtml?: string;
  presenter?: string;
  outcome?: string;
  vote?: VoteTally | null;
  /** OPTIONAL staff-only pacing estimate (minutes) for this agenda item — 0/absent = unset.
   *  Bounded by MaxTimeboxMinutes (600) server-side. STAFF-ONLY: never rendered on the public
   *  meeting page — only the staff builder + printable/dais agenda. */
  timeboxMinutes?: number;
  createdAt: string;
  updatedAt: string;
}

/** An attachment is EITHER an uploaded file (`s3Key` set) OR an external link (`linkUrl` set) —
 *  never both (agenda-packet.md §10A attach-by-link/replace). `isLink` mirrors which one server-side
 *  for cheap client rendering without re-deriving it from field presence. */
export interface MeetingAttachmentView {
  id: string;
  tenantId: string;
  meetingId: string;
  itemId: string;
  s3Key?: string;
  linkUrl?: string;
  isLink: boolean;
  filename?: string;
  contentType?: string;
  description: string;
  accessible: MeetingAttachmentAccessible;
  visibility: Visibility;
  createdAt: string;
  /** Empty for an item-level (clerk) attachment; set to the department key for a
   *  contribution-scoped attachment (agenda-packet.md §3.1). */
  contributionDepartment?: string;
  /** True when this attachment is grouped under the item's collapsed "Supporting documents"
   *  expander on the public page instead of rendering inline in the agenda outline (Council Agenda
   *  10A supporting-docs feature). Defaults falsy/undefined for pre-existing attachments — those
   *  render inline exactly as before. */
  supporting?: boolean;
}

/** One attachment reference in a SummarizeAgendaItemResult's used/skipped lists. */
export interface SummarizeAttachmentRef {
  id: string;
  filename: string;
  /** Present only in `skipped` — a human-readable reason the file wasn't read. */
  reason?: string;
}

/** Result of POST /api/admin/meetings/{id}/items/{itemId}/summarize — the AI's
 *  proposed item narrative (sanitized HTML) plus which attachments were read and
 *  which were skipped (and why). The clerk reviews/edits before saving. */
export interface SummarizeAgendaItemResult {
  summaryHtml: string;
  used: SummarizeAttachmentRef[];
  skipped: SummarizeAttachmentRef[];
}

export type ContributionState = "requested" | "drafted" | "submitted" | "included";

export const CONTRIBUTION_STATE_LABELS: Record<ContributionState, string> = {
  requested: "Requested",
  drafted: "Drafted",
  submitted: "Submitted",
  included: "Included",
};

/** Client-side mirror of shared.CanTransitionContribution (agenda-packet.md §3.1 state diagram) —
 *  a UX affordance only; the server re-validates on write (the actual trust boundary). */
const CONTRIBUTION_TRANSITIONS: Record<ContributionState, ContributionState[]> = {
  requested: ["drafted", "submitted"],
  drafted: ["submitted"],
  submitted: ["drafted", "included"],
  included: ["submitted"],
};

export function CAN_TRANSITION_CONTRIBUTION(
  from: ContributionState,
  to: ContributionState,
): boolean {
  if (from === to) return false;
  return CONTRIBUTION_TRANSITIONS[from]?.includes(to) ?? false;
}

export interface ContributionView {
  id: string;
  tenantId: string;
  meetingId: string;
  itemId: string;
  department: string;
  assigneeId?: string;
  state: ContributionState;
  reportHtml?: string;
  requestedBy?: string;
  requestedAt: string;
  submittedAt?: string;
  updatedAt: string;
}

/** "draft" | "adopted" — Minutes.AdoptionState (agenda-packet.md §0.4); "" from the wire reads as
 *  "draft" (ResolvedAdoptionState's zero-value discipline — mirrored client-side too). */
export type AdoptionState = "draft" | "adopted";

export interface MinutesView {
  meetingId: string;
  tenantId: string;
  bodyHtml?: string;
  summaryHtml?: string;
  /** LEGACY single publish flag — superseded by recapPublished/minutesPublished below (recap +
   *  minutes independent-publish feature). Still returned for backward compat; the staff UI should
   *  read recapPublished/minutesPublished (which the server already resolves against this legacy
   *  flag for any pre-split item — see shared.Minutes.ResolvedRecapPublished/ResolvedMinutesPublished
   *  server-side), not `published`, going forward. */
  published: boolean;
  /** Resolved (server-side) independent publish gates — recap (summaryHtml) has no adoption
   *  dependency; minutes (bodyHtml) is additionally gated by adoption state + the tenant's
   *  "show draft minutes" setting on the PUBLIC read path (this field just reflects the STAFF-side
   *  publish flag, not final public visibility). */
  recapPublished: boolean;
  minutesPublished: boolean;
  adoptionState?: AdoptionState | "";
  /** ISO timestamp the clerk marked minutes adopted (best-effort provenance) — "" until adopted. */
  adoptedAt?: string;
  recordingKind?: string;
  youTubeUrl?: string;
  recordingS3Key?: string;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/admin/meetings/{id} — the full staff bundle (handler.MeetingByID mirror). */
export interface MeetingBundle {
  meeting: MeetingView;
  items: AgendaItemView[];
  attachments: MeetingAttachmentView[];
  contributions: ContributionView[];
  minutes: MinutesView | null;
  events: AuditEventView[];
}

export interface CreateMeetingInput {
  body: string;
  title?: string;
  meetingDate: string;
  startTime?: string;
  location: string;
}

export type UpdateMeetingInput = CreateMeetingInput;

/** POST /api/admin/meetings/{id}/publish body (agenda-packet.md §10A tentative/final). Optional —
 *  omitted defaults to "tentative" on a draft publish; explicitly set to "final" to mark an
 *  already-published meeting final without touching its published state. */
export interface PublishMeetingInput {
  agendaState?: "tentative" | "final";
}

export interface AgendaItemInput {
  title: string;
  narrativeHtml?: string;
  presenter?: string;
  outcome?: string;
  vote?: VoteTally | null;
  /** OPTIONAL staff-only pacing estimate (minutes) — 0/omitted = unset. See AgendaItemView. */
  timeboxMinutes?: number;
}

/** POST .../items body — CreateAgendaItemInput mirror. parentItemId is OPTIONAL: omitted/"" creates
 *  a TOP-LEVEL item (a "section"); set it to an EXISTING top-level item's id to create a first-class
 *  sub-item under it (Council Agenda 10A). The server rejects a parentItemId naming an item that is
 *  ITSELF a sub-item (hard two-level cap). */
export interface CreateAgendaItemInput extends AgendaItemInput {
  parentItemId?: string;
}

/** POST .../items/reorder body — the staff drag/keyboard tree reorder + reparent (Council Agenda
 *  10A). Top-level entries at the array root; sub-items nested under `children` (NO deeper nesting —
 *  ReorderAgendaSubItemPosition has no children field of its own, matching the Go type). Every
 *  current agenda item (top-level + sub-item) must be named EXACTLY ONCE across the whole payload. */
export interface ReorderAgendaSubItemPosition {
  id: string;
  order: number;
}

export interface ReorderAgendaItemPosition {
  id: string;
  parentId?: string;
  order: number;
  children?: ReorderAgendaSubItemPosition[];
}

export interface ReorderAgendaItemsPayload {
  items: ReorderAgendaItemPosition[];
}

/** POST .../items/{itemId}/attachments body — EXACTLY ONE of `s3Key` (an already-uploaded file, the
 *  `uploadDocument` -> strip-leading-slash flow) or `linkUrl` (an external http/https URL, e.g. a
 *  SharePoint "anyone with the link" share — the server rejects non-http(s) schemes). Set
 *  `replaceAttachmentId` to update an EXISTING attachment in place (keeps its position in the item's
 *  attachment list) instead of creating a new one. */
export interface AttachMeetingFileInput {
  s3Key?: string;
  linkUrl?: string;
  filename?: string;
  contentType?: string;
  description: string;
  accessible: MeetingAttachmentAccessible;
  replaceAttachmentId?: string;
  /** When set, REMOVES that attachment from the item and ignores every content field above — the
   *  same POST endpoint, mirroring `replaceAttachmentId`. Used by `removeMeetingFile`. */
  deleteAttachmentId?: string;
  /** Marks this attachment (or, on a Replace call, moves the existing attachment) into/out of the
   *  item's collapsed "Supporting documents" group on the public page. Omitted/false = renders
   *  inline in the agenda outline, unchanged from before. */
  supporting?: boolean;
}

/** The closed set of MANUAL, MEETING-LEVEL attachment link types (mirrors shared.MeetingLinkType* —
 *  handler.AttachMeetingLink derives the display label/badge from this server-side; the client never
 *  sends free-text). */
export type MeetingLinkType = "agenda" | "packet" | "minutes" | "recap" | "video" | "other";

/** The linkType -> display-label picker OPTIONS shown in the "Attach document" form — mirrors
 *  store.MeetingLinkDescription's mapping exactly (that's what the server actually stores as the
 *  attachment's Description once this posts; "other" -> "Document" there, not "Other"). */
export const MEETING_LINK_TYPE_LABELS: Record<MeetingLinkType, string> = {
  agenda: "Agenda",
  packet: "Packet",
  minutes: "Minutes",
  recap: "Recap",
  video: "Video",
  other: "Document (other)",
};

/** POST /api/admin/meetings/{id}/attachments body (handler.AttachMeetingLink) — a MANUAL,
 *  MEETING-LEVEL attach (not tied to an agenda item) for a clerk backfilling a document the
 *  historical importer missed, OR just attaching a document straight to the meeting. EXACTLY ONE of
 *  `linkUrl` (an external http/https URL — server derives the attachment's display label from
 *  `linkType`, required in this mode) or `s3Key` (a file already uploaded via the shared
 *  `uploadDocument` presign — `linkType` is ignored in this mode; the display label falls back to
 *  `filename`, or "Document" if that's empty too). */
export interface AttachMeetingLinkInput {
  linkUrl?: string;
  linkType?: MeetingLinkType;
  s3Key?: string;
  filename?: string;
  contentType?: string;
}

export interface UpsertMinutesInput {
  bodyHtml?: string;
  summaryHtml?: string;
  /** LEGACY — sets BOTH publishRecap and publishMinutes when those are omitted (server-side
   *  backward-compat mapping, see shared.UpsertMinutesInput's doc comment). New callers should use
   *  the two independent fields below instead. */
  publish?: boolean;
  /** Publishes the plain-language recap (summaryHtml) — no adoption dependency, one-directional
   *  (never clears an already-published recap on a plain content-edit save). */
  publishRecap?: boolean;
  /** Publishes the formal minutes (bodyHtml) — one-directional. Public visibility is ADDITIONALLY
   *  gated by adoption state + the tenant's "show draft minutes" setting (server-enforced; this flag
   *  alone doesn't guarantee public visibility while still in draft). */
  publishMinutes?: boolean;
}

export interface AssignContributionInput {
  department: string;
  assigneeId?: string;
}

/** Clerk on-behalf-of save (PUT .../contributions/{department}) — transitionTo, when given, must be
 *  a legal ContributionState move from the slot's current state (server-validated). */
export interface SaveContributionInput {
  reportHtml?: string;
  transitionTo?: ContributionState;
}

/** AI "Draft from last meeting" proposal — {sections:[{title,narrativeHtml}]}, agenda-packet.md
 *  §5.1. Never auto-applied; the clerk accepts/edits each section into a real agenda item. */
export interface DraftAgendaSection {
  title: string;
  narrativeHtml: string;
}

/** POST .../draft-agenda request body (agenda-packet.md §5.1). `priorTranscript` is optional —
 *  a clerk-pasted transcript of the prior meeting's recording (e.g. YouTube's "Show transcript")
 *  that the AI uses alongside the prior minutes to sharpen Old Business / Action Items. Both
 *  fields are omitted/empty-string safe (purely additive; backend field name is exact). */
export interface DraftAgendaRequest {
  carryOverHint?: string;
  priorTranscript?: string;
}

// ---- Council Agenda + Packet — 10B recording->minutes/summary pipeline -----------------------
// Mirrors services/api/internal/shared/schemas.go's TranscriptJob / TranscribeVocabulary +
// handler.{meeting_transcribe,transcribe_vocabulary}.go's wire shapes 1:1 — no drift.

/** TranscriptJob.State (agenda-packet.md §5.2) — "" (no TranscriptJob yet, the AdminMeetingTranscribeStatus
 *  "no recording provided" case) reads as no pipeline started; never rendered as an error. */
export type TranscriptJobState = "" | "queued" | "transcribing" | "drafting" | "ready" | "failed";

/** GET /api/admin/meetings/{id}/transcribe/status response (handler.AdminMeetingTranscribeStatus). */
export interface TranscriptJobView {
  state: TranscriptJobState;
  error?: string;
  startedAt?: string;
  updatedAt?: string;
}

/** POST /api/admin/meetings/{id}/recording/upload-init body (shared.RecordingUploadInitInput). */
export interface RecordingUploadInitInput {
  filename: string;
  contentType: string;
}

/** POST .../recording/upload-init response — a presigned S3 PUT (store.PresignRecordingUpload). */
export interface RecordingUploadPresign {
  uploadUrl: string;
  s3Key: string;
}

/** POST /api/admin/meetings/{id}/transcribe body (shared.TranscribeMeetingInput). EITHER an uploaded
 *  recording's s3Key (audio-upload fallback) OR a pasted transcript string (the PRIMARY path — Linn
 *  Valley pastes the YouTube auto-transcript). A pasted-transcript job starts at state "drafting"
 *  (no separate transcribing step) and flows through the same status polling + minutes-review UI. */
export type TranscribeMeetingInput = { recordingS3Key: string } | { transcript: string };

/** Audio-only content-type allowlist mirror (shared.RecordingUploadContentTypes) — used to build the
 *  file input's `accept` attribute; the server is the actual trust boundary/validator. */
export const RECORDING_UPLOAD_ACCEPT = "audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/ogg";

/** Hosted-document content-type allowlist mirror (shared.DocUploadContentTypes — pdf, common image
 *  scan formats, and the macro-free OOXML formats a clerk assembles a council packet from) — used to
 *  build a "Upload a file" input's `accept` attribute (admin-doc-upload / meeting-level attach's
 *  upload mode); the server is the actual trust boundary/validator. */
export const DOC_UPLOAD_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/webp,image/gif," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";

// ---- YouTube transcript integration (2026-08-21 reversal) --------------------------------------
// Mirrors handler.youtube_oauth.go + handler.meeting_youtube_transcript.go's wire shapes 1:1.

/** GET /api/admin/youtube/status response (handler.AdminYouTubeStatus). `channelTitle` is present
 *  only when connected AND the connect-time channel lookup succeeded (best-effort server-side). */
export interface YouTubeStatusView {
  connected: boolean;
  channelTitle?: string;
}

/** GET /api/admin/youtube/connect response (handler.AdminYouTubeConnect) — the caller does a full
 *  browser redirect (`window.location.href = authorizeUrl`), NOT a fetch; this is an OAuth
 *  consent-screen hop, not an API call the SPA can complete in-place. */
export interface YouTubeAuthorizeView {
  authorizeUrl: string;
}

/** `source` on a successful YouTube transcript pull (handler.AdminMeetingYouTubeTranscript) —
 *  "youtube_manual" = a real human-authored caption track; "youtube_asr" = YouTube's
 *  auto-generated captions. Surfaced to the clerk so they know which kind backed the draft. */
export type YouTubeTranscriptSource = "youtube_manual" | "youtube_auto";

/** POST /api/admin/meetings/{id}/youtube-transcript response (202). Lands in the SAME
 *  drafting-slot a pasted transcript does (stageTranscriptIntoDraftingSlot) — the returned
 *  `transcript` text is shown back to the clerk mainly so the UI can say "here's what was pulled",
 *  not for the caller to resubmit it anywhere. */
export interface YouTubeTranscriptPullResult {
  state: TranscriptJobState;
  source: YouTubeTranscriptSource;
  transcript: string;
}

/** The closed set of distinguishable error codes AdminMeetingYouTubeTranscript can return (task
 *  brief) — callers branch on `ApiError.code`, mapping each to a specific, actionable message
 *  rather than a generic failure toast. */
export type YouTubeTranscriptErrorCode =
  | "youtube_not_configured"
  | "no_youtube_link"
  | "youtube_not_connected"
  | "no_caption_tracks"
  | "asr_download_failed"
  | "youtube_unavailable";

// ---- Export packet — merged agenda + attachments PDF for emailing council --------------------

/** POST /api/admin/meetings/{id}/packet body — the client-generated agenda (+ "Meeting materials"
 *  index page) PDF, base64-encoded, handed to the server to merge with every attachment it CAN fetch
 *  (uploaded files; many external links a browser can't reach cross-origin, hence the client-side
 *  materials index as the fallback list).
 *
 *  `convertedDocx` (optional): the server has no docx renderer, so a same-origin .docx attachment is
 *  converted to PDF CLIENT-SIDE (mammoth docx->HTML -> the shared html->pdfmake pipeline, see
 *  staff/docxToPdf.ts) before this POST, then uploaded via the existing presigned-document-upload
 *  flow. Maps each successfully-converted attachment's id -> the S3 key of its uploaded PDF rendering,
 *  so the server merges THAT instead of skipping the original .docx. An attachment the client couldn't
 *  convert (an external-link docx, a fetch/parse failure) is simply omitted here — the server reports
 *  it in `skipped` with its own reason, same as any other unmergeable attachment. */
export interface ExportPacketInput {
  agendaPdfBase64: string;
  convertedDocx?: Record<string, string>;
}

/** One line of the packet response's `included`/`skipped` manifests. */
export interface PacketManifestEntry {
  title: string;
  type: string;
}

/** POST .../packet response — a presigned GET for the assembled packet PDF, plus a manifest of what
 *  did/didn't make it into the merge (a link-type attachment the server can't fetch shows up in
 *  `skipped` with a reason; it's still listed on the agenda PDF's "Meeting materials" page). */
export interface ExportPacketResult {
  downloadUrl: string;
  filename: string;
  included: PacketManifestEntry[];
  skipped: (PacketManifestEntry & { reason: string })[];
}

// ---- Public /meetings (meetings_public.go allowlist projections) -----------------------------

/** One typed, meeting-level document link on the public LIST card (meetings-public-relaunch.md
 *  §1/§5 — "like linnvalley.gov/government/meetings/", separate labeled Agenda/Packet/Minutes/
 *  Recap/Watch links per meeting instead of a single "Minutes posted" badge). `label` is the EXACT
 *  render string the server picked (meetings_public.go's `publicMeetingDocumentLabel` — note
 *  `type: "video"` renders as **"Watch"**, not "Video"). Only present types are ever included, in
 *  the server's own natural order (client re-sorts defensively to the fixed
 *  Agenda→Packet→Minutes→Recap→Watch order per spec §1.4, since the server doesn't guarantee it). */
export interface PublicMeetingDocLink {
  type: "agenda" | "packet" | "minutes" | "recap" | "video" | "other";
  label: string;
  url: string;
  /** True when `url` is an IN-PAGE deep-link to this SAME meeting's detail page (e.g.
   *  "/meetings/<id>/#recap"), not an external file/link — added so the list can offer a
   *  "Recap"/"Minutes" affordance for a published entity even when the town never attached a
   *  separate Recap/Minutes DOCUMENT (Lister/LV team, 2026-08-28 — "fast access to recap/minutes
   *  from the list + in-page bookmarks"). Omitted/false for every existing document-backed link. */
  isPage?: boolean;
}

export interface PublicMeetingView {
  id: string;
  body: string;
  title?: string;
  meetingDate: string;
  startTime?: string;
  location: string;
  status: "published" | "archived";
  agendaState?: AgendaState;
  /** @deprecated superseded by `documents` (meetings-public-relaunch.md §5) — Minutes is now just
   *  one entry in `documents`, not a standalone badge. Kept only because the server still sends it
   *  (back-compat field, never read by the redesigned list). */
  hasMinutes?: boolean;
  /** Meeting-level typed doc links (Agenda/Packet/Minutes/Recap/Watch) — always an array (possibly
   *  empty), only the types that actually exist on this meeting, never a placeholder for a missing
   *  one (meetings-public-relaunch.md §1.4). */
  documents: PublicMeetingDocLink[];
}

export interface PublicAttachmentView {
  id: string;
  filename?: string;
  contentType?: string;
  description: string;
  accessible: MeetingAttachmentAccessible;
  /** The resolved download/link URL, server-set — NOT derived client-side. For a FILE attachment
   *  this is the public CloudFront download path ("/" + the doc's S3 key, same convention as
   *  document_upload.go/branding.go's publicPath); for a LINK attachment (`isLink: true`) this is the
   *  external http(s) URL as entered. May be empty/missing (e.g. the builder Preview, which has no
   *  live URL for a not-yet-published attachment) — never render an href in that case. */
  url?: string;
  /** True when this attachment is an external link (not a hosted copy) — render with an
   *  external-link affordance instead of a plain download. */
  isLink: boolean;
  /** True when this attachment belongs in the item's collapsed "Supporting documents" expander
   *  instead of the inline agenda outline (Council Agenda 10A). Omitted/false = inline, unchanged. */
  supporting?: boolean;
}

/** children carries this item's SUB-ITEMS (Council Agenda 10A nested sub-items) — OMITTED when
 *  there are none, never an empty array (byte-exact mirror of handler/meetings_public.go's
 *  `children,omitempty`). ONE LEVEL of nesting only: a sub-item's own `children` is always
 *  undefined/omitted (two-level cap, server-enforced). */
export interface PublicAgendaItemView {
  id: string;
  order: number;
  title: string;
  narrativeHtml?: string;
  presenter?: string;
  outcome?: string;
  vote?: VoteTally | null;
  attachments: PublicAttachmentView[];
  children?: PublicAgendaItemView[];
}

export interface PublicMinutesView {
  bodyHtml?: string;
  summaryHtml?: string;
  adoptionState: AdoptionState;
}

export interface PublicMeetingDetail {
  meeting: PublicMeetingView;
  items: PublicAgendaItemView[];
  minutes: PublicMinutesView | null;
}

// ---- Public "Reported Concerns" map (public-concerns-map.md / ADR-0023) -----------------------
//
// GET /api/public/concerns — no auth, PII-free. Byte-exact mirror of handler/concerns_public.go's
// GeoJSON FeatureCollection: each Feature's `properties` is a shared.PublicConcernPin (opaque
// salted-hash `ref`, category LABEL, collapsed 3-way public status, fuzzed [lng,lat], ISO
// dateReported, nullable ISO dateResolved). Coordinates are ALREADY fuzzed server-side — never
// imply precision in the UI (the mockup's "Pin locations are approximate" note).

/** The THREE public-facing statuses (never the full internal lifecycle) — collapsed server-side by
 *  shared.PublicConcernStatus. Distinct from @td/shared's RequestStatus (the internal taxonomy). */
export type PublicConcernStatus = "open" | "in-progress" | "resolved";

export interface PublicConcernPin {
  /** Opaque salted-hash token (handler.concernRef) — NEVER the real tracking code or internal id. */
  ref: string;
  /** Human-readable category LABEL (e.g. "Junk / abandoned vehicle") — display text only, never a
   *  filter/URL-param value (that's `categoryId` below). */
  category: string;
  /** Raw category id (e.g. "c-1783978501467") — the SAME value the `category` query param / embed
   *  deep-link (`?category=<id>`) and the server-side filter use. Optional only for backward
   *  compatibility with an older backend response shape; once universally deployed this is always
   *  present. The `<select>` in ConcernsMapPage.tsx uses this as its option `value` (never the
   *  label) so the filter and the URL param stay id-based end to end. */
  categoryId?: string;
  /** Fixed-vocabulary issue type (concerns-map-redesign-spec.md's "what is this" axis — distinct
   *  from `category`, which is the department that owns it). One of: "Construction cleanup",
   *  "Road repair", "Meter location", "Service connection", "Hydrant", "Billing", or empty/absent
   *  when not set. Rendered NEUTRAL (ink + outline chip, never color-coded) so `status` stays the
   *  only hue-coded signal on this page. */
  type?: string;
  status: PublicConcernStatus;
  /** Fuzzed, non-reversible coordinates (shared.FuzzCoord) — render as approximate, always. */
  lat: number;
  lng: number;
  /** PII-safe street-level label (e.g. "Oak St") — NO house number, NEVER an exact address. Empty
   *  string/absent when no street could be safely extracted server-side; render as "—", never blank
   *  with no context (ADR-0023's two-tier location: fuzzed pin + this block-level text). */
  location?: string;
  dateReported: string;
  dateResolved: string | null;
}

export interface PublicConcernsSummary {
  reported: number;
  resolved: number;
}

/** The mapped client view model (api/concernsMap.ts's geoJSONToConcernsView) — flattens the wire
 *  GeoJSON FeatureCollection into `pins` + `summary` + the k-anonymity `suppressed` flag
 *  (ADR-0023 decision 7: below the tenant's MinCount, the server returns pins:[] + aggregate counts
 *  only + `suppressed: true` — the UI must render gracefully, never assume a non-empty feed). */
export interface PublicConcernsView {
  pins: PublicConcernPin[];
  summary: PublicConcernsSummary;
  suppressed: boolean;
}

export interface PublicConcernsFilter {
  category?: string;
  status?: PublicConcernStatus;
  from?: string;
  to?: string;
}

// ---- STAFF concerns map (staff-concerns-map.md) -----------------------------------------------
//
// GET /api/requests/map — Cognito-authed (concerns:read, same ModuleConcerns/ActionRead permission
// the concern queue itself requires), EXACT (un-fuzzed) coordinates, ALL
// located concerns incl. staffOnly/internal categories, full lifecycle status (never collapsed to
// the public's 3-way scope). The internal counterpart to PublicConcernPin/PublicConcernsView above
// — deliberately a SEPARATE type (not a superset reusing PublicConcernPin) since the privacy
// postures are opposite: this view is allowed to carry the real id/trackingCode/exact address, the
// public one must never gain those fields by a future edit to a shared type.

/** One concern pin on the staff map — exact coordinates, full fields, click-through to
 *  /staff/reports/:id via `id`. `status` is the FULL internal lifecycle (@td/shared RequestStatus),
 *  not the public's collapsed 3-way scope — staff need to tell New/Acknowledged/Closed/Duplicate/
 *  Invalid apart, not just open/in-progress/resolved. */
export interface StaffConcernPin {
  id: string;
  trackingCode: string;
  /** Human-readable category label — display text only (mirrors PublicConcernPin.category). */
  category: string;
  /** Raw category id — the filter/URL-param value (mirrors PublicConcernPin.categoryId). */
  categoryId?: string;
  /** Fixed-vocabulary issue type (mirrors PublicConcernPin.type). */
  type?: string;
  status: RequestStatus;
  lat: number;
  lng: number;
  /** PII-safe street-level label — kept for parity with the public shape's "near {street}" line,
   *  but staff ALSO get `address` below (the exact house-number address), unlike residents. */
  location?: string;
  /** Exact street address (NOT fuzzed/truncated) — staff-only field, never sent to the public feed. */
  address?: string;
  /** StaffOnly/internal-category concerns (work-item-due-dates.md) are visually distinguished on
   *  this map (a ring/badge) so staff can tell internal engineering items from resident-facing
   *  reports at a glance. */
  staffOnly: boolean;
  /** Assignee display name, when assigned. */
  assignee?: string;
  /** Line/infrastructure reference (water-project-progress-layer.md groundwork) — optional, present
   *  only for concerns tied to a specific infrastructure line. */
  lineId?: string;
  /** External system reference (e.g. a case/work-order id in another system), when set. */
  externalRef?: string;
  dateReported: string;
  dateResolved: string | null;
}

export interface StaffConcernsView {
  pins: StaffConcernPin[];
}

export interface StaffConcernsFilter {
  category?: string;
  status?: RequestStatus;
  from?: string;
  to?: string;
}

// ---- Contributor "My reports" (contributor.go allowlist projections) -------------------------

export interface ContributorMeetingView {
  id: string;
  body: string;
  title?: string;
  meetingDate: string;
  startTime?: string;
  location: string;
  status: MeetingStatus;
}

export interface ContributorAgendaItemView {
  id: string;
  order: number;
  title: string;
}

export interface ContributorContributionView {
  id: string;
  meetingId: string;
  itemId: string;
  department: string;
  state: ContributionState;
  reportHtml?: string;
  requestedAt: string;
  submittedAt?: string;
  updatedAt: string;
}

export interface MyReportSlot {
  contribution: ContributorContributionView;
  meeting: ContributorMeetingView;
  item: ContributorAgendaItemView;
}

// ---- Council Meetings historical importer — staff console (meetings-embed-and-import.md §2) ----
//
// Mirrors the shape Inky's pinned contract returns exactly (§2's 4 endpoints). Screens 1-3 operate
// entirely on the in-memory MeetingImportDetail the polling GET returns — nothing writes to the
// database until POST .../commit (review-gate rule #1, §2.3).

/** One detected document link on an extracted row — same artifact types the public badge row
 *  renders (§3 "public list ↔ import review-table visual parity"). */
export type MeetingImportLinkType = "agenda" | "packet" | "minutes" | "recap" | "watch";

export interface MeetingImportLink {
  url: string;
  text: string;
  type: MeetingImportLinkType;
  confidence: number;
}

export type MeetingImportConfidence = "high" | "medium" | "low";

/** Buckets the extractor's raw 0-1 confidence score into the three-level status-pill the review
 *  table renders (§2.2 screen 3 — "always a text label, never color-only", §4). */
export function meetingImportConfidenceLevel(score: number): MeetingImportConfidence {
  if (score >= 0.85) return "high";
  if (score >= 0.5) return "medium";
  return "low";
}

/** Flag reasons the extractor itself can raise on a row (§2.3 rule #3 — never silently
 *  auto-corrected, always surfaced for a human decision). */
export type MeetingImportFlag = "low_confidence" | "not_a_meeting" | "duplicate";

export interface MeetingImportRow {
  rowId: string;
  meetingDate: string; // ISO "YYYY-MM-DD" (may be a low-confidence parse — see flags)
  body: string;
  title?: string;
  confidence: number; // 0-1, see meetingImportConfidenceLevel
  flags: MeetingImportFlag[];
  flagNote?: string; // the clerk-facing explanation for the flag (mockup's flagcell text)
  duplicateOfMeetingId?: string;
  duplicateOfLabel?: string; // e.g. the existing meeting's title, for the dupecell copy
  included: boolean;
  links: MeetingImportLink[];
}

export type MeetingImportStatus = "extracting" | "ready" | "failed";

/** Response of POST /api/admin/meetings/import (screen 1 submit). */
export interface StartMeetingImportResult {
  importId: string;
  status: MeetingImportStatus;
}

export interface MeetingImportCounts {
  total: number;
  included: number;
  flagged: number;
  duplicates: number;
}

/** Response of GET /api/admin/meetings/import/{importId} — the poll target for screen 2, and the
 *  full data source for screen 3's review table. */
export interface MeetingImportDetail {
  importId: string;
  sourceUrl: string;
  body: string;
  status: MeetingImportStatus;
  error?: string;
  meetings: MeetingImportRow[];
  counts: MeetingImportCounts;
}

export interface StartMeetingImportInput {
  sourceUrl: string;
  body?: string;
  dateRange?: string;
}

/** Body of PATCH .../rows/{rowId} — the review table's inline edit + include/exclude toggle
 *  (§2.2 screen 3). Any subset of fields; the server returns the row's fresh state. */
export interface UpdateMeetingImportRowInput {
  meetingDate?: string;
  body?: string;
  included?: boolean;
  links?: MeetingImportLink[];
}

/** How documents are stored on commit (§2.2 screen 4's one real decision, §7.1 — link-to-existing
 *  is the recommended default; re-host is a fast-follow opt-in). */
export type MeetingImportStoreMode = "link" | "rehost";

/** Response of POST .../commit (screen 5's stats). */
export interface CommitMeetingImportResult {
  createdCount: number;
  mergedCount: number;
  skippedCount: number;
}

// ---- Ordinance Library — Phase B4 staff console (ordinance-library-phaseB.md §1.3/§1.5) --------
//
// Mirrors services/api/internal/shared/schemas.go's CodeVersion/CodeNode/ParseNote/CrossRef wire
// shapes one-for-one (field names + JSON tags match exactly) — this console reads/writes those
// structs verbatim, no client-side reshaping.

/** A parse-time note surfaced by the extractor (ADR-0014's mandatory-review surface). Only
 *  severity="warn" notes are BLOCKING for publish; "info" notes never gate. */
export interface OrdinanceParseNote {
  file?: string;
  anchor?: string;
  severity: "info" | "warn";
  message: string;
}

export interface OrdinanceCrossRef {
  toPermalink: string;
  rawText: string;
  resolved: boolean;
}

/** One node in the Title>Chapter>Article>Section tree, scoped to a version. */
export interface OrdinanceCodeNode {
  tenantId: string;
  versionId: string;
  nodeId: string;
  type: "title" | "chapter" | "article" | "section";
  number: string;
  heading: string;
  permalink: string;
  bodyHtml?: string;
  parentId?: string;
  order: number;
  adoptedBy?: string;
  history?: string;
  crossRefs?: OrdinanceCrossRef[];
}

export type OrdinanceVersionStatus = "draft" | "previewing" | "live" | "superseded";

export interface OrdinanceCodeVersion {
  tenantId: string;
  versionId: string;
  label: string;
  sourceFormat: string; // word|pdf|html
  status: OrdinanceVersionStatus;
  isLive: boolean;
  sectionCount: number;
  publishedAt?: string;
  publishedBy?: string;
  createdAt: string;
  createdBy?: string;
  parseNotes?: OrdinanceParseNote[];
  warningCount: number;
  // Phase B3 server-side review gate (ADR-0014 §1.5) — mutated ONLY by the review endpoints, read
  // by the publish gate. A fresh draft always starts wholly unreviewed.
  reviewedSectionPermalinks?: string[];
  addressedWarningIndexes?: number[];
}

/** Response of POST /api/admin/ordinances/ingest (B2 draft-ingest). */
export interface OrdinanceDraftIngestResult {
  versionId: string;
  sectionCount: number;
  warningCount: number;
  parseNotes: OrdinanceParseNote[];
}

/** Response of GET /api/admin/ordinances/{versionId} — the SAME read the publish gate uses, so the
 *  review screen shows exactly what publish will decide (phaseB.md §1.5). */
export interface OrdinanceVersionDetail {
  version: OrdinanceCodeVersion;
  nodes: OrdinanceCodeNode[];
  publishable: boolean;
  blockedReason: string;
}

/** Response of POST /api/admin/ordinances/{versionId}/publish. */
export interface OrdinancePublishResult {
  versionId: string;
  status: "live";
  supersededId: string;
  sitePages: number;
  filesSynced: number;
  corpusSections: number;
  invalidationId: string;
  siteWarning?: string;
}

/** Response of POST .../review/sections and .../review/warnings — both echo the version's current
 *  review-progress state so the client always re-derives gate status from server truth. */
export interface OrdinanceReviewProgress {
  versionId: string;
  status: OrdinanceVersionStatus;
  reviewedSectionPermalinks?: string[];
  addressedWarningIndexes?: number[];
}

// ---- Ordinance Library — R3 cross-module reference layer (spec §3.9 "Referenced by this
// platform") --------------------------------------------------------------------------------
//
// Lets staff attach a live ordinance § to a Request (concern/case/permit — one shared entity)
// or an FAQ answer, ADDITIVE alongside Code Enforcement's existing free-text `ordinanceRef`
// field (that field never changes). Mirrors the backend's OrdinanceRef wire shape one-for-one.

/** The staff/admin-facing entity types an ordinance § can be attached to. A "case"/"permit" is a
 *  Request on the live path (same storage, see CaseDetailPage/PermitDetailPage doc comments) but
 *  the ordinance-refs route is scoped per the URL the detail page already reads from. */
export type OrdinanceRefEntityType =
  "requests" | "questions" | "kora" | "cases" | "permits" | "faq";

/** One attached citation — a live, structured link from a Request/FAQ to an ordinance §,
 *  alongside (never replacing) any free-text ordinance reference on the entity. */
export interface OrdinanceRef {
  tenantId: string;
  entityType: OrdinanceRefEntityType;
  entityId: string;
  permalink: string;
  sectionLabel: string;
  createdBy?: string;
  createdAt: string;
}

/** Staff reverse lookup — GET /api/admin/ordinance/refs?permalink=… — every entity (with id) that
 *  cites one section, for the "Cited by" panel next to a live/draft §. */
export interface OrdinanceRefsReverseResult {
  permalink: string;
  refs: OrdinanceRef[];
}

/** Public reverse lookup — GET /api/public/ordinance/refs?permalink=… — COUNTS ONLY, never entity
 *  ids or case/reporter detail (complainant-privacy non-negotiable). Keys are OrdinanceRefEntityType
 *  values that actually appear; a type with zero refs is simply absent. */
export interface OrdinanceRefsPublicCounts {
  permalink: string;
  counts: Partial<Record<OrdinanceRefEntityType, number>>;
}

/** One downvoted question surfaced in the staff "Answer quality" panel — a resident's own free-
 *  text question is never shown publicly (complainant-privacy posture carries over even though
 *  Ask-the-code has no reporter identity attached; this is staff-only, Cognito-authed). */
export interface OrdinanceAskDownvote {
  query: string;
  sections: string[];
  confident: boolean;
  createdAt: string;
}

/** GET /api/admin/ordinance/ratings — the public "Was this helpful?" 👍/👎 tally on Ask-the-code
 *  answers (ordinance-ask.js), plus the most recent downvoted questions for a clerk to spot-check
 *  where the grounded answer let a resident down. */
export interface OrdinanceRatingsSummary {
  version: string;
  up: number;
  down: number;
  recentDownvotes: OrdinanceAskDownvote[];
}

/** One § search hit from the ordinance ask/search surface (the picker reuses the same public
 *  ask endpoint the "/code" viewer's question-first hero calls — officialText[] entries are
 *  exactly the candidate sections a staff member can pick to attach). */
export interface OrdinanceSearchHit {
  permalink: string;
  number: string;
  heading: string;
  text: string;
}

// ---- Contractor Registry (docs/design/contractor-registry/spec.md, PR 2/4 — web) ----------------
//
// Mirrors services/api/internal/shared/schemas.go's Contractor/PublicContractor/PutContractorInput
// field-for-field (spec §2/§4). ContractorStatus is DERIVED server-side (EvaluateContractorStatus) —
// the client never computes Expired itself; it only reads whichever status the server already
// returned (either on the raw `contractor.status` list field, which IS the effective value per
// handler.AdminContractors's list projection, or the detail endpoint's separate `effectiveStatus`).

export type ContractorStatus = "Approved" | "Pending" | "Suspended" | "Expired";
export type ContractorVisibility = "Public" | "Private";

export const CONTRACTOR_STATUSES: ContractorStatus[] = ["Approved", "Pending", "Suspended", "Expired"];

export const CONTRACTOR_STATUS_LABELS: Record<ContractorStatus, string> = {
  Approved: "Approved",
  Pending: "Pending",
  Suspended: "Suspended",
  Expired: "Expired",
};

/** The full staff-side Contractor record — field-for-field mirror of shared.Contractor (spec §2's
 *  table). Internal-only fields are present here (this type is NEVER used on the public surface —
 *  see PublicContractorView below for that fixed, narrow projection). */
export interface ContractorView {
  id: string;
  tenantId: string;
  businessName: string;
  contactName: string;
  phone: string;
  email?: string;
  mailingAddress?: string;
  registrationNumber: string;
  trades: string[];
  status: ContractorStatus;
  visibility: ContractorVisibility;
  insuranceCarrier?: string;
  insurancePolicyNumber?: string;
  insuranceCertExpiresAt?: string;
  bondAmount?: string;
  bondSurety?: string;
  registrationDate?: string;
  notes?: string;
  lastReminderAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** One row in the staff Contractors list (GET /api/admin/contractors) — the stored Contractor plus
 *  the server-DERIVED effectiveStatus (spec §3a/§6 — Expired is computed from the insurance-cert
 *  clock at read time, never mutated into storage by a batch job). */
export interface ContractorQueueRow {
  contractor: ContractorView;
  effectiveStatus: ContractorStatus;
}

/** GET/PUT /api/admin/contractors/{id} detail response (handler.ContractorDetailView) — the
 *  contractor, its derived effective status, and the "permits pulled by this contractor"
 *  reverse-reference (spec §3b) — EMPTY until PR 3 wires Request.ContractorID, never hidden (the
 *  section always renders, with an explicit empty state per the spec's acceptance criteria). Permit
 *  rows here are intentionally untyped beyond what the detail page actually reads (id/trackingCode/
 *  description/status/location/issuedAt) rather than importing the full StaffPermitDetail shape. */
export interface ContractorPermitRef {
  id: string;
  trackingCode?: string;
  description?: string;
  status?: string;
  location?: { address?: string };
  issuedAt?: string;
  createdAt?: string;
}

export interface ContractorDetailView {
  contractor: ContractorView;
  effectiveStatus: ContractorStatus;
  permits: ContractorPermitRef[];
}

/** POST /api/admin/contractors (create) + PUT /api/admin/contractors/{id} (update) body — mirrors
 *  shared.PutContractorInput field-for-field (spec §3d). TenantID/ID/CreatedAt/UpdatedAt are never
 *  sent from the client (server-resolved, ADR-0003/NFR-11). */
export interface PutContractorInput {
  businessName: string;
  contactName: string;
  phone: string;
  email?: string;
  mailingAddress?: string;
  registrationNumber: string;
  trades: string[];
  status: ContractorStatus;
  visibility: ContractorVisibility;
  insuranceCarrier?: string;
  insurancePolicyNumber?: string;
  insuranceCertExpiresAt?: string;
  bondAmount?: string;
  bondSurety?: string;
  registrationDate?: string;
}

/** POST /api/admin/contractors/{id}/renew body (spec §3b "Renew insurance cert"). */
export interface RenewContractorInput {
  insuranceCertExpiresAt: string;
}

/** The PUBLIC-safe field set (spec §4) — business/contact/phone/trades + a bare "currently
 *  registered" flag, NO dates, NO internal fields of any kind. Mirrors shared.PublicContractor
 *  exactly; this is the ONLY shape the public directory/picker ever renders from — never derived
 *  client-side from a fuller ContractorView (the server enforces the visibility+status predicate,
 *  §3c's "render only what the API returns"). */
export interface PublicContractorView {
  id: string;
  businessName: string;
  contactName: string;
  phone: string;
  trades: string[];
  registered: true;
}
